import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Calendar, Users, Sparkles, Zap, Clock, DollarSign, ArrowRight, Package, Activity, Shield, FileText, Code, Key, Fingerprint, Lock, TrendingUp, Star, Link2, QrCode, Camera, Bell, Download, Mail, Menu, X, Globe, Bitcoin, BadgeDollarSign, Percent, SlidersHorizontal, BarChart3, Anchor, MapPin, Database, ArrowLeftRight, Megaphone, MousePointerClick, Compass, MessageCircle, LifeBuoy, CloudSun, Waves, Wind, Moon, RefreshCw, FileSpreadsheet, Handshake, ChevronDown } from 'lucide-react';
import { BeaconIcon } from '../components/BeaconIcon';
import { QRCodeSVG as QRCodeComponent } from 'qrcode.react';
import { getAllPlans, formatPrice } from '../config/pricing';
import { SocialLinksDark } from '../components/SocialLinks';
import HelpScoutBeacon from '../components/HelpScoutBeacon';
import WhatsAppFloat from '../components/WhatsAppFloat';
import { FloatingNavbar } from '../components/landing/FloatingNavbar';
import { LogosBlurFlip } from '../components/landing/LogosBlurFlip';
import { NoiseBackground } from '../components/ui/noise-background';
import { NoiseStripsHero } from '../components/ui/noise-strips-hero';
import { StatsSection } from '../components/landing/StatsSection';
import { GlowingCardsSection } from '../components/landing/GlowingCardsSection';
import { EmbedTerminal } from '../components/landing/EmbedTerminal';
import { SparklesCore } from '../components/ui/sparkles';
import { StickyScroll } from '../components/ui/sticky-scroll-reveal';
import { TracingBeam } from '../components/ui/tracing-beam';
import { WorldMap } from '../components/ui/world-map';
import { BackgroundBeamsWithCollision } from '../components/ui/background-beams-with-collision';
import { FaqSection } from '../components/landing/FaqSection';
import { getInitialLang, setStoredLang } from '../utils/uaeLocale';
import { initMetaPixel } from '../utils/analytics';
import { KioskDirectSection } from '../components/KioskDirectSection';
import { proxyImg } from '../utils/img';

// Auto-rotating top announcement banner slides (module scope = stable identity).
const ANNOUNCEMENTS = [
  {
    href: '/customers/boatx',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'BOATX processed $100k+ in bookings',
    rest: '\u00A0through BookingFlows',
    cta: '— see how',
  },
  {
    href: '/customers/banana-boat-rentals',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'Welcoming Banana Boat Rentals',
    rest: '\u00A0— 15+ boat fleet now managed on BookingFlows',
    cta: 'read their story',
  },
  {
    href: '/meet-the-team',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'USCG · RYA · MCA · IYT · ASA',
    rest: '\u00A0— built by certified captains',
    cta: 'meet the team',
  },
];

const SupplierFeaturesPage = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(getInitialLang);
  const isAr = lang === 'ar';
  const switchLang = (l) => { setLang(l); setStoredLang(l); };
  const [bannerVisible, setBannerVisible] = useState(true);
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  const [announceIdx, setAnnounceIdx] = useState(0);
  const [announcePaused, setAnnouncePaused] = useState(false);
  useEffect(() => { initMetaPixel(); }, []);

  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined' &&
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || announcePaused) return;
    const id = setInterval(() => {
      setAnnounceIdx((i) => (i + 1) % ANNOUNCEMENTS.length);
    }, 6000);
    return () => clearInterval(id);
  }, [announcePaused]);

  const trackSignupStarted = (source) => {
    import('../utils/analytics').then((m) => {
      if (typeof m.trackSignupStarted === 'function') {
        m.trackSignupStarted(source);
      }
    }).catch(() => {});
  };

  useEffect(() => {
    import('../utils/analytics').then(({ trackSupplierPageView }) => {
      trackSupplierPageView('/features');
    });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('animate-fade-in-up');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -100px 0px' });
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    const onScroll = () => {
      const y = window.scrollY;
      setBannerVisible(y < 120);
      setShowFloatingCta(y > 700);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div className={`min-h-screen bg-white flex flex-col ${isAr ? 'lang-ar' : ''}`} data-testid="features-page">
      {isAr ? <WhatsAppFloat /> : <HelpScoutBeacon />}
      <Helmet>
        <html lang={isAr ? 'ar' : 'en'} />
        <title>All Features — Booking Software for Tours, Charters &amp; Rentals | BookingFlows</title>
        <meta name="description" content="The full BookingFlows feature tour — payments, QuickBooks sync, waivers, embeds, CRM, lead capture, security, audit trails, white-labeling and more." />
        <link rel="canonical" href="https://bookingflows.com/features" />
      </Helmet>
      <style>{`
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .animate-fade-in-up {
          opacity: 1;
          transform: translateY(0);
        }
        html {
          scroll-behavior: smooth;
        }
      `}</style>

      {/* Top announcement banner — auto-rotating crossfade (BOATX blue ⇄ Banana
          Boat Rentals yellow ⇄ credential ribbon). Fixed height (h-11) so every
          slide stays vertically centered on all screens. Pauses on hover;
          auto-hides on scroll past the hero. */}
      <div
        data-testid="supplier-top-banner"
        className={`relative overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          bannerVisible ? 'h-11 max-h-12 opacity-100' : 'h-11 max-h-0 opacity-0'
        }`}
        onMouseEnter={() => setAnnouncePaused(true)}
        onMouseLeave={() => setAnnouncePaused(false)}
      >
        {ANNOUNCEMENTS.map((a, i) => (
          <a
            key={i}
            href={a.href}
            data-testid={`top-banner-slide-${i}`}
            aria-hidden={i !== announceIdx}
            className={`absolute inset-0 flex items-center justify-center text-center text-xs sm:text-sm font-medium tracking-wide leading-none transition-opacity duration-700 ease-out ${a.bg} ${a.text} ${
              i === announceIdx ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 flex items-center justify-center gap-1.5 sm:gap-2">
              <span>
                <span className="font-semibold">{a.bold}</span>
                <span className="hidden sm:inline">{a.rest}</span>
              </span>
              <span className="opacity-90 underline-offset-2 hover:underline whitespace-nowrap inline-flex items-center gap-1 shrink-0">
                {a.cta}
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </a>
        ))}
      </div>

      <FloatingNavbar isAr={isAr} switchLang={switchLang} />

      {/* Compact header — the landing page is the highlight reel; this is
          the whole catalog. */}
      <section className="relative bg-white pt-14 sm:pt-16 pb-2">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5" data-testid="features-header-badge" dir={isAr ? 'rtl' : undefined}>
            <Package className="w-3.5 h-3.5" />
            {isAr ? 'جولة الميزات الكاملة' : 'The full feature tour'}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.05] mb-6" data-testid="features-header-title" dir={isAr ? 'rtl' : undefined}>
            {isAr ? <>كل ما تحت الغطاء.</> : <>Everything under the hood.</>}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto mb-8" dir={isAr ? 'rtl' : undefined}>
            {isAr ? 'الصفحة الرئيسية هي المختصر — وهذه هي القائمة الكاملة. المدفوعات، والمحاسبة، والتسويق، والأمان، وكل التفاصيل التشغيلية.' : 'The landing page is the highlight reel — this is the whole catalog. Payments, accounting, marketing, security, and the operational deep cuts.'}
          </p>
          <button
            onClick={() => { trackSignupStarted('features_header'); navigate('/signup'); }}
            className="btn-supplier-primary inline-flex items-center text-base font-semibold px-7 py-3.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/25"
            data-testid="features-header-signup"
          >
            {isAr ? 'ابدأ مجاناً' : 'Start free'} <ArrowRight className={isAr ? 'mr-2 rotate-180 w-4 h-4' : 'ml-2 w-4 h-4'} />
          </button>
        </div>
      </section>

      {/* Platform stats — count-up proof points under the infra logos. */}
      <StatsSection isAr={isAr} />

      {/* What's inside — Aceternity glowing-effect card grid. */}
      <GlowingCardsSection isAr={isAr} />

      {/* 💚 QuickBooks Online sync — LIVE. Official Intuit QuickBooks Online
          integration (production keys). Positioning per founder: official,
          primed and professional — integrated, not overclaiming partnership. */}
      <section className="py-24 bg-white" data-testid="supplier-quickbooks-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center gap-2 text-[#2CA01C] text-xs font-semibold uppercase tracking-[0.2em] mb-6" data-testid="quickbooks-official-badge">
            <RefreshCw className="w-3.5 h-3.5" />
            Official integration · Intuit QuickBooks Online
          </div>
          <img
            src="/images/bf-quickbooks-connect.webp"
            alt="BookingFlows connected to Intuit QuickBooks Online — bookings, waivers, weather and crew on one side, invoices, banking and reports on the other"
            loading="lazy"
            data-testid="quickbooks-section-visual"
            className="w-full block max-w-3xl mx-auto mb-14"
          />
          <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.12] mb-8" dir={isAr ? 'rtl' : undefined}>
            {isAr ? <>دفاترك المحاسبية،{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2CA01C] to-emerald-500">على الطيار الآلي.</span></> : <>Your books,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2CA01C] to-emerald-500">on autopilot.</span></>}
          </h3>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-10" dir={isAr ? 'rtl' : undefined}>
            {isAr ? 'BookingFlows متكامل رسمياً مع Intuit QuickBooks Online. اربط شركتك مرة واحدة — مصروفات طاقمك تُسجّل كمشتريات مصنّفة، والحجوزات المدفوعة تتجمع في إيصالات مبيعات يومية نظيفة، وكل بند يصل إلى شجرة حساباتك أنت. مزامنة ليلية، بلا تكرار، وبلا إعادة إدخال في نهاية الشهر.' : <>BookingFlows is officially integrated with Intuit QuickBooks Online. Connect your
            company once — your crew's expense uploads post as categorized Purchases, paid
            bookings roll up into clean daily Sales Receipts, and every line lands in your own
            chart of accounts. Synced nightly, nothing duplicated, no month-end retyping.</>}
          </p>
          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10 text-left" data-testid="quickbooks-feature-grid">
            {[
              ['Crew-logged expenses', 'Captains snap receipts at the dock — QuickBooks gets a categorized Purchase that night.'],
              ['Daily revenue receipts', 'Paid bookings roll into one Sales Receipt per day. Clean P&L, zero retyping.'],
              ['Your accounts, respected', 'Categories map to your own chart of accounts — in USD, AED or any home currency.'],
            ].map(([t, d]) => (
              <div key={t} className="rounded-xl bg-gray-50/60 p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Check className="w-4 h-4 text-[#2CA01C] flex-shrink-0" />
                  <span className="text-sm font-bold text-gray-900">{t}</span>
                </div>
                <p className="text-[13px] text-gray-500 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate('/blog/quickbooks-sync')}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2CA01C] hover:text-[#108000] transition-colors"
            data-testid="quickbooks-section-cta"
          >
            See how the sync works
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="quickbooks-trust-row">
            {[
              [Lock, 'Secure OAuth connection'],
              [Shield, 'Encrypted tokens, bank-grade security'],
              [Check, 'Disconnect anytime, from either side'],
            ].map(([Icon, t]) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <Icon className="w-3.5 h-3.5 text-[#2CA01C]" />
                {t}
              </span>
            ))}
          </div>
          <div className="mt-4">
            <a
              href="https://quickbooks.intuit.com/partners/affiliates/?cid=par_pim_Qxq0keD8r71c"
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid="quickbooks-affiliate-link"
              className="text-sm text-gray-500 hover:text-[#2CA01C] underline underline-offset-4 transition-colors"
            >
              Don&apos;t use QuickBooks yet? Get QuickBooks Online &rarr;
            </a>
          </div>
          <p className="mt-10 text-[11px] text-gray-400">
            Intuit and QuickBooks are registered trademarks of Intuit Inc. Used with permission.
          </p>
        </div>
      </section>

      {/* Embeddable Booking Widget Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Text Content - Centered on top */}
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Code className="w-3.5 h-3.5" />
              Your Website
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
              {isAr ? <>ضمّن الحجوزات في{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">موقعك الخاص.</span></> : <>Embed bookings on{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">your own site.</span></>}
            </h3>
            <p className="text-lg text-gray-500 mb-6 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'يحجز عملاؤك مباشرة من موقعك. بلا إعادة توجيه. بلا عوائق.' : 'Customers book directly from your website. No redirects. No friction.'}
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Copy & paste install</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Brand colors</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Mobile-responsive</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Real-time sync</span>
              </div>
            </div>
          </div>

          {/* Terminal demo — the embed story as a live install session */}
          <EmbedTerminal />

          {/* Embed renders — one snippet fanning out to every surface, then
              the widget living on a real operator site (white-on-white) */}
          <div className="max-w-5xl mx-auto">
            <img
              src="/images/bf-embeds-2.webp"
              alt="One embed snippet powering BookingFlows booking widgets across desktop, mobile and calendar views"
              className="w-full block"
              loading="lazy"
              data-testid="embed-section-visual-1"
            />
            <img
              src="/images/bf-embeds.webp"
              alt="BookingFlows widget embedded on an operator's own website, on laptop and phone, with the embed code alongside"
              className="w-full block mt-2 sm:mt-4"
              loading="lazy"
              data-testid="embed-section-visual-2"
            />
          </div>
        </div>
      </section>

      {/* Waiver Management — converted from dark to light to break the
          dark-stack rhythm. The Shareable Customer Tickets section below
          covers the customer-facing side; this is the legal/audit angle. */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Shield className="w-3.5 h-3.5" />
              Legal Protection
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
              {isAr ? <>تنازلات{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">تصمد فعلاً.</span></> : <>Waivers that{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">actually hold up.</span></>}
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'بصمات الأجهزة، وتسجيل عناوين IP، وسجلات تدقيق — لتملك إثباتاً حقيقياً إن احتجت إليه يوماً.' : 'Device fingerprints, IP capture, and audit trails — so you have real proof if you ever need it.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-7 md:order-2">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <Fingerprint className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Device Verification</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Capture device fingerprints, IP addresses, and browser info automatically when customers sign. Full audit trail for legal protection.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">PIN-Based Tickets</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Guides get a unique PIN for each trip. Customers enter the PIN to access their ticket — guides instantly know who signed.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Custom Waiver Text</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Write your own waiver text. Multi-signature support for groups. Customers sign on their phone before the trip.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative md:order-1">
              <img
                src="/images/bf-waivers.webp"
                alt="BookingFlows digital waivers — signed and verified records on tablet and phone"
                loading="lazy"
                data-testid="waivers-section-visual"
                className="w-full block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* BookingFlows Beacon + Beacon Tender — the pixel that powers HELM's
          acquisition reports and feeds retargeting Campaigns. */}
      <section className="py-24 bg-white" data-testid="supplier-beacon-section">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy + snippet + the loop — right side on desktop */}
            <div className="min-w-0 lg:order-2">
              <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-emerald-50 mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700">BookingFlows Beacon · Your own pixel</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-gray-900 mb-5" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>البكسل الخاص بك.<br />موصول مباشرة بحجوزاتك.</> : <>Your own pixel.<br />Wired straight into your bookings.</>}
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7 max-w-xl" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'مثل Google Analytics أو Meta Pixel — لكنه ملكك، وموصول مباشرة بحجوزاتك. سطر واحد من الكود على موقعك وستعرف بالضبط أي قنوات التسويق تنتج حجوزات فعلية — لا مجرد نقرات.' : <>Think Google Analytics or Meta Pixel — except it's yours, wired
                straight into your bookings. One line of code on your website and you
                know exactly which marketing channels produce bookings — not just clicks.</>}
              </p>

              {/* The loop: visitors → audiences → bookings */}
              <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="beacon-loop-strip">
                {[
                  { icon: BeaconIcon, label: 'Understand your visitors' },
                  { icon: BarChart3, label: 'Build audiences' },
                  { icon: Megaphone, label: 'Win more bookings' },
                ].map((s, i) => (
                  <React.Fragment key={s.label}>
                    {i > 0 && <ArrowRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />}
                    <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#EBF2FF] text-[#2563EB] text-xs font-bold">
                      <s.icon className="w-3.5 h-3.5" strokeWidth={2} />
                      {s.label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
              <p className="text-sm text-gray-500 leading-relaxed max-w-xl">
                That's the whole loop — see where visitors come from, turn them into
                audiences, and bring them back for a few dollars a day. No agencies,
                no ad platforms, no exporting data.
              </p>
            </div>

            {/* Beacon render — left side on desktop */}
            <div className="relative min-w-0 w-full lg:order-1">
              <img
                src="/images/bf-beacon.webp"
                alt="BookingFlows Beacon — live visitor events streaming from your website into HELM"
                loading="lazy"
                data-testid="beacon-section-visual"
                className="w-full block"
              />
              <a href="https://chromewebstore.google.com/detail/bookingflows-beacon-tende/lomkgfganpkdkeeeipjdngkcmhbbahkh"
                target="_blank" rel="noopener noreferrer" data-testid="beacon-tender-store-link"
                className="group mt-2 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors">
                <BeaconIcon className="w-4 h-4" />
                Beacon Tender — Add to Chrome
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Kiosk Direct — Walk-ins 2.0: dockside tablet → paid booking + signed waiver */}
      <KioskDirectSection onCta={() => { trackSignupStarted('supplier_kiosk_direct_section'); navigate('/signup'); }} />

      {/* Data migration — switching-anxiety killer. Sits right after pricing
          where "but I already have bookings in another system" objections peak. */}
      <section className="py-24 bg-white animate-on-scroll" data-testid="supplier-migration-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
            {/* Left: Copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <ArrowLeftRight className="w-3.5 h-3.5" />
                Switching Is Easy
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>لديك رحلات محجوزة أصلاً؟{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">سننقل كل شيء.</span></> : <>Already have trips booked?{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  We'll migrate everything.
                </span></>}
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'الحجوزات القائمة، والعملاء، والجداول، وبطاقات الهدايا — مهندسونا ينقلونها كلها من أجلك. نقل البيانات عملية برمجية معتادة ومجرّبة لفريقنا، وليست مشروعاً عليك إدارته. وتواصل استقبال الحجوزات طوال الوقت.' : <>Existing bookings, customers, schedules, gift cards — our engineers move it
                all for you. Data migration is a standard, well-worn software process for our
                team, not a project you have to manage. You keep taking bookings the entire time.</>}
              </p>
              <div className="space-y-3">
                {[
                  'Upcoming trips & past booking history come with you',
                  'Customers, waivers & gift card balances preserved',
                  'Handled end-to-end by our engineers — typically days, not weeks',
                  'Zero downtime — keep selling while we move you over',
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/onboarding')}
                data-testid="migration-onboarding-link"
                className="inline-flex items-center gap-1.5 mt-7 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                See the full onboarding guide <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Right: 3-step migration card */}
            <div>
              <div className="bg-gradient-to-br from-gray-50 to-blue-50/40 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="supplier-migration-steps">
                {/* Animated transfer strip — old system → BookingFlows */}
                <div className="flex items-center gap-3 mb-7 rounded-xl bg-white ring-1 ring-slate-900/[0.05] px-4 py-3.5" aria-hidden="true">
                  <div className="flex items-center gap-2.5 shrink-0">
                    <span className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center">
                      <Database className="w-4 h-4" />
                    </span>
                    <div>
                      <div className="text-[11px] font-bold text-slate-700 leading-tight">Old system</div>
                      <div className="text-[10px] text-slate-400">any export</div>
                    </div>
                  </div>
                  <div className="relative flex-1 h-px bg-gradient-to-r from-slate-200 via-blue-300 to-blue-500 mx-1">
                    <span className="bf-migrate-dot" />
                    <span className="bf-migrate-dot" style={{ animationDelay: '0.8s' }} />
                    <span className="bf-migrate-dot" style={{ animationDelay: '1.6s' }} />
                    <span className="absolute left-1/2 -translate-x-1/2 -top-[13px] w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-500/40">
                      <Zap className="w-3.5 h-3.5" fill="currentColor" />
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    <div className="text-right">
                      <div className="text-[11px] font-bold text-blue-700 leading-tight">BookingFlows</div>
                      <div className="text-[10px] text-slate-400">zero downtime</div>
                    </div>
                    <span className="w-9 h-9 rounded-xl bg-[#EBF2FF] flex items-center justify-center">
                      <img src="/logo-icon.png" className="w-5 h-5 rounded" alt="" />
                    </span>
                  </div>
                </div>

                <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400 font-semibold mb-5 flex items-center gap-2">
                  <Database className="w-3.5 h-3.5" /> How Migration Works
                </p>
                <div className="space-y-5">
                  {[
                    { step: '1', title: 'Share an export', desc: 'A spreadsheet or export from your current system — any format works.' },
                    { step: '2', title: 'Our engineers map & import', desc: 'Trips, customers, schedules and balances land exactly where they belong.' },
                    { step: '3', title: 'Verify together & go live', desc: 'We walk through it with you side by side. Flip the switch when it all looks right.' },
                  ].map((s, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0 shadow-sm">
                        {s.step}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">{s.title}</p>
                        <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t border-gray-200/70 flex items-center gap-2.5">
                  <Shield className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <p className="text-xs text-gray-500">Migration is included — no extra fees, no "implementation packages."</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dedicated Support — Help Center + advanced ticketing + Bosun AI */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="supplier-support-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy — right side on desktop (visual flipped to the left) */}
            <div className="lg:order-2">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EBF2FF] mb-6">
                <LifeBuoy className="w-3.5 h-3.5 text-[#2563EB]" strokeWidth={2.25} />
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#2563EB]">Support Center · A real team behind you</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>دعم متقدّم.{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">لا ثقب أسود.</span></> : <>Advanced support.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Not a black hole.
                </span></>}
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'يجيب Bosun عن الأسئلة السريعة في ثوانٍ — ولكل ما عداها، تحصل شركتك على مركز دعم مخصص: أدلة قابلة للبحث لكل ميزة، ونظام تذاكر بمستوى المؤسسات يراسلك في كل خطوة، من «استلمنا طلبك» إلى «تم الحل».' : <>Bosun answers the quick questions in seconds — and for everything else, your
                company gets a dedicated Support Center: searchable guides for every feature,
                plus an enterprise ticketing system that emails you at every step, from
                "we've got it" to "it's resolved."</>}
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'A full Help Center — guides for bookings, waivers, payments, kiosk & more',
                  'Tracked support tickets with email updates at every status change',
                  'Real engineers on the other end — the people who built the platform',
                ].map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">{line}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/support')}
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
                data-testid="supplier-support-cta"
              >
                Visit the Support Center
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            {/* Support Center mock — left side on desktop */}
            <div className="relative max-w-md mx-auto lg:mx-0 lg:mr-auto w-full lg:order-1" aria-hidden="true">
              <div className="absolute -top-5 left-6 z-10 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white shadow-md">
                <span className="w-5 h-5 rounded-md bg-[#2563EB] text-white flex items-center justify-center">
                  <LifeBuoy className="w-3 h-3" />
                </span>
                <span className="text-xs font-bold text-slate-900">Support <span className="italic">Center</span> <span className="font-normal text-slate-400">by BookingFlows</span></span>
              </div>
              <div className="rounded-[1.5rem] bg-gray-50 p-6 pt-8">
                {/* Ticket card */}
                <div className="rounded-2xl bg-white p-4 mb-4">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="font-mono text-[11px] text-[#2563EB]/70 font-bold">BFSUP-4F2A1C</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                      <Check className="w-2.5 h-2.5" strokeWidth={3} /> Resolved
                    </span>
                  </div>
                  <div className="text-sm font-bold text-slate-900 mb-3">Refund not showing on booking #8841</div>
                  {/* Status timeline */}
                  <div className="space-y-2.5">
                    {[
                      { label: 'Ticket received — confirmation emailed', t: '9:02 AM' },
                      { label: 'Team reviewing your ticket', t: '9:14 AM' },
                      { label: 'Resolved — refund reissued via Stripe', t: '10:47 AM' },
                    ].map((s, i) => (
                      <div key={s.label} className="flex items-center gap-2.5">
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${i === 2 ? 'bg-emerald-500 text-white' : 'bg-[#EBF2FF] text-[#2563EB]'}`}>
                          <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                        </span>
                        <span className="text-[12px] font-medium text-slate-600 flex-1">{s.label}</span>
                        <span className="text-[10px] text-slate-400">{s.t}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Help center strip */}
                <div className="flex items-center gap-2.5 rounded-xl bg-white px-3.5 py-3">
                  <Mail className="w-4 h-4 text-[#2563EB] shrink-0" />
                  <span className="text-[12px] font-medium text-slate-600">Email updates at every step — nothing disappears into a queue.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────────────────────────────────────────────
          Zapier — every confirmed booking can trigger a Zap in 8,000+ apps.
          Truthful: live feature (Marketing → Integrations), fires after
          Stripe confirms payment, no code required.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-orange-50/40 to-white" data-testid="supplier-zapier-section">
        <div className="pointer-events-none absolute top-16 right-[10%] h-72 w-72 rounded-full bg-orange-100/50 blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy */}
            <div className="animate-on-scroll">
              <div className="inline-flex items-center gap-2 text-[#FF4F00] text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M14.13 12.01c0 .62-.11 1.22-.32 1.77-.55.21-1.15.33-1.78.33h-.01c-.63 0-1.23-.12-1.78-.33-.21-.55-.33-1.15-.33-1.77v-.01c0-.63.12-1.23.33-1.78.55-.21 1.15-.33 1.78-.33h.01c.63 0 1.23.12 1.78.33.21.55.32 1.15.32 1.78v.01zm7.6-1.35h-5.71l4.04-4.04a10.6 10.6 0 0 0-1.07-1.26 10.6 10.6 0 0 0-1.26-1.07l-4.04 4.04V2.62c-.54-.09-1.1-.14-1.66-.14h-.01c-.57 0-1.12.05-1.67.14v5.71L6.31 4.29c-.45.32-.87.68-1.26 1.07-.39.39-.75.81-1.07 1.26l4.04 4.04H2.31s-.14 1.1-.14 1.67v.01c0 .57.05 1.12.14 1.66h5.71l-4.04 4.04c.64.9 1.43 1.69 2.33 2.33l4.04-4.04v5.71c.54.09 1.1.14 1.66.14h.02c.56 0 1.12-.05 1.66-.14v-5.71l4.04 4.04c.45-.32.87-.68 1.26-1.07.39-.39.75-.81 1.07-1.26l-4.04-4.04h5.71c.09-.54.14-1.1.14-1.66v-.02c0-.56-.05-1.12-.14-1.66z" />
                </svg>
                Zapier integration
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>كل حجز، في كل مكان.<span className="block text-gray-400">Zapier يوصلك بأكثر من 8,000 تطبيق.</span></> : <>Every booking, everywhere.
                <span className="block text-gray-400">Zapier connects you to 8,000+ apps.</span></>}
              </h2>
              <p className="mt-5 text-lg text-gray-600 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'لحظة دفع أي حجز، يمكن لـ BookingFlows إطلاق الحدث إلى أي Zap — صف جديد في Google Sheets، أو تنبيه Slack لقناة الطاقم، أو جهة اتصال في Mailchimp، أو فاتورة Xero. والعملاء المحتملون الجدد أيضاً.' : <>The moment a checkout is paid, BookingFlows can fire the event into any Zap —
                a new row in Google Sheets, a Slack ping to the crew channel, a Mailchimp
                contact, a Xero invoice. New leads too.</>}
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Fires only after Stripe confirms the payment — never an unpaid checkout',
                  'No code: paste one Zapier webhook URL and you\u2019re live',
                  'Send a test event with one click to map your fields',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#FF4F00] shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 111.4-1.4l2.8 2.79 6.8-6.79a1 1 0 011.4 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{t}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { trackSignupStarted('supplier_zapier_section'); navigate('/signup'); }}
                data-testid="supplier-zapier-signup"
                className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#FF4F00] hover:text-[#E64700] transition-colors">
                Automate your bookings
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            {/* Zap-flow mock */}
            <div className="animate-on-scroll">
              <div className="bg-white rounded-2xl shadow-[0_25px_60px_-25px_rgba(15,23,42,0.25)] p-6 sm:p-8" data-testid="supplier-zapier-mock">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Live event stream
                </div>
                {/* Event card */}
                <div className="rounded-xl bg-blue-50/50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src="/logo-icon.png" alt="BookingFlows" className="h-8 w-8 rounded-lg shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">booking.confirmed</div>
                        <div className="text-xs text-gray-500 truncate">Sunset Cruise · 4 guests · $420 paid</div>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-2 py-0.5 shrink-0">PAID</span>
                  </div>
                </div>
                {/* Connector */}
                <div className="flex flex-col items-center py-1.5">
                  <div className="h-4 w-px bg-gray-200" />
                  <svg className="w-6 h-6 text-[#FF4F00]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M14.13 12.01c0 .62-.11 1.22-.32 1.77-.55.21-1.15.33-1.78.33h-.01c-.63 0-1.23-.12-1.78-.33-.21-.55-.33-1.15-.33-1.77v-.01c0-.63.12-1.23.33-1.78.55-.21 1.15-.33 1.78-.33h.01c.63 0 1.23.12 1.78.33.21.55.32 1.15.32 1.78v.01zm7.6-1.35h-5.71l4.04-4.04a10.6 10.6 0 0 0-1.07-1.26 10.6 10.6 0 0 0-1.26-1.07l-4.04 4.04V2.62c-.54-.09-1.1-.14-1.66-.14h-.01c-.57 0-1.12.05-1.67.14v5.71L6.31 4.29c-.45.32-.87.68-1.26 1.07-.39.39-.75.81-1.07 1.26l4.04 4.04H2.31s-.14 1.1-.14 1.67v.01c0 .57.05 1.12.14 1.66h5.71l-4.04 4.04c.64.9 1.43 1.69 2.33 2.33l4.04-4.04v5.71c.54.09 1.1.14 1.66.14h.02c.56 0 1.12-.05 1.66-.14v-5.71l4.04 4.04c.45-.32.87-.68 1.26-1.07.39-.39.75-.81 1.07-1.26l-4.04-4.04h5.71c.09-.54.14-1.1.14-1.66v-.02c0-.56-.05-1.12-.14-1.66z" />
                  </svg>
                  <div className="h-4 w-px bg-gray-200" />
                </div>
                {/* Destination chips */}
                <div className="grid grid-cols-2 gap-2.5">
                  {[
                    ['Google Sheets', 'New row added', '#0F9D58'],
                    ['Slack', '#bookings pinged', '#611F69'],
                    ['Mailchimp', 'Contact created', '#FFE01B'],
                    ['Xero', 'Invoice raised', '#13B5EA'],
                  ].map(([app, action, color]) => (
                    <div key={app} className="rounded-xl bg-gray-50/70 px-3.5 py-3 flex items-center gap-2.5">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-gray-800 truncate">{app}</div>
                        <div className="text-[11px] text-gray-400 truncate">{action}</div>
                      </div>
                      <svg className="w-3.5 h-3.5 text-emerald-500 ml-auto shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 111.4-1.4l2.8 2.79 6.8-6.79a1 1 0 011.4 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-[11px] text-gray-400 text-center">
                  and 8,000+ more apps via Zapier — set up in minutes, no code
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* API — for operators with a tech team (and the devs evaluating for them).
          MOVED up under the Zapier section per founder — visual LEFT, copy right. */}
      <section className="py-24 bg-white" data-testid="supplier-api-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <img
                src="/images/bf-api.webp"
                alt="BookingFlows developer API — clean JSON responses for bookings, services, assets, team and waivers"
                loading="lazy"
                data-testid="api-section-visual"
                className="w-full block"
              />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Code className="w-3.5 h-3.5" />
                Developer API · Included on every plan
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>بياناتك،{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">عند الطلب.</span></> : <>Your data,{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">on tap.</span></>}
              </h3>
              <p className="text-lg text-gray-500 leading-relaxed mb-8 max-w-xl" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'واجهة REST API نظيفة للقراءة فقط للحجوزات والخدمات والأصول والفريق والتنازلات الموقّعة. معظم المشغّلين لا يلمسونها أبداً — لكن محاسبك أو وكالتك أو لوحتك المخصصة ستحب وجودها.' : <>A clean, read-only REST API for bookings, services, assets, team and signed waivers.
                Most operators never touch it — but your accountant, agency or custom dashboard will love that it's there.</>}
              </p>
              <div className="space-y-4 mb-8">
                {[
                  ['Keys you control', 'Create keys in Settings, grant each one only the permissions the integration needs, revoke instantly.'],
                  ['Privacy-first JSON', 'Responses stay within your own operational records — internal and compliance data never leaves the platform.'],
                  ['Secure, scoped API access', 'Header-only authentication, keys shown once and securely stored, every request scoped to your organization.'],
                ].map(([title, desc]) => (
                  <div key={title} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      <span className="font-semibold text-gray-900">{title}.</span> {desc}
                    </p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/developers')}
                data-testid="landing-api-docs-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
              >
                View the API docs
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* The money side — Reports + per-asset revenue + P&L as one
          sticky-scroll story (replaces the old Reports & Revenue Insights). */}
      <section className="py-24 bg-white" data-testid="supplier-money-story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <FileSpreadsheet className="w-3.5 h-3.5" />
              The money side · Every plan
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
              {isAr ? <>كل درهم،{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">محسوب.</span></> : <>Every dollar,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">accounted for.</span></>}
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'تقارير، وإيرادات لكل أصل، وربحية واضحة — تُجمع لك، لا بواسطتك.' : 'Reports, per-asset earnings and a clear P&L — compiled for you, not by you.'}
            </p>
          </div>
          <StickyScroll
            content={[
              {
                badge: 'Reports',
                title: 'Reports your accountant will love',
                description: 'Daily, weekly, monthly — or any range you pick. Professional trips & money reports, compiled for you and exported as clean .csv files. No copy-pasting into spreadsheets, ever.',
                content: (
                  <div className="flex h-full w-full items-center justify-center rounded-3xl bg-gray-50 p-6">
                    <img src="/images/bf-reports.webp" alt="BookingFlows reports" loading="lazy" className="w-full block" data-testid="money-story-reports-visual" />
                  </div>
                ),
              },
              {
                badge: 'Fleet earnings',
                title: 'Know which assets actually pay the bills',
                description: 'Every boat, jet ski or vehicle shows its trailing 12-month revenue — pulled from real bookings, not guesswork. Spot the ones carrying the fleet and the ones quietly costing you.',
                content: (
                  <div className="flex h-full w-full items-center justify-center rounded-3xl bg-gray-50 p-6">
                    <div className="w-full max-w-sm rounded-2xl bg-white p-5" data-testid="money-story-assets-visual">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-bold text-gray-900">Assets</span>
                        <span className="text-[10px] font-bold text-blue-700 bg-blue-50 rounded-full px-2.5 py-1">Showing revenue</span>
                      </div>
                      {[
                        { n: 'Sunset Cruiser', v: '$38,420', w: '100%', top: true },
                        { n: 'Harbor Pontoon', v: '$24,180', w: '63%' },
                        { n: 'Wave Runner', v: '$3,960', w: '10%', low: true },
                      ].map((a) => (
                        <div key={a.n} className="py-2.5">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[13px] font-semibold text-gray-800">{a.n}</span>
                            <span className={'text-[13px] font-bold tabular-nums ' + (a.low ? 'text-gray-400' : 'text-gray-900')}>{a.v}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                            <div className={'h-full rounded-full ' + (a.top ? 'bg-blue-600' : a.low ? 'bg-gray-300' : 'bg-blue-400')} style={{ width: a.w }} />
                          </div>
                        </div>
                      ))}
                      <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] text-amber-600 font-medium flex items-center gap-1.5">
                        <Activity className="w-3 h-3" /> Wave Runner is underperforming
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                badge: 'P&L',
                title: 'Profitability that is always current',
                description: 'Revenue, expense and asset breakdowns totaled for you — collected, owed, add-ons and refunds. Understand your P&L at a glance, any day of the month.',
                content: (
                  <div className="flex h-full w-full items-center justify-center rounded-3xl bg-gray-50 p-6">
                    <div className="w-full max-w-sm rounded-2xl bg-white p-5" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="money-story-pnl-visual">
                      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 mb-4">This month · live</div>
                      {[
                        ['Revenue collected', '$61,240', 'text-gray-900'],
                        ['Add-ons & tips', '+$4,180', 'text-gray-900'],
                        ['Refunds', '-$1,420', 'text-gray-400'],
                        ['Expenses', '-$18,655', 'text-gray-400'],
                      ].map(([k, v, c]) => (
                        <div key={k} className="flex items-center justify-between py-2 text-sm">
                          <span className="text-gray-500">{k}</span>
                          <span className={'font-semibold ' + c}>{v}</span>
                        </div>
                      ))}
                      <div className="mt-3 rounded-xl bg-emerald-50 px-4 py-3 flex items-center justify-between">
                        <span className="text-sm font-bold text-emerald-800">Net profit</span>
                        <span className="text-lg font-extrabold text-emerald-700">$45,345</span>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                badge: 'Export',
                title: 'Custom, filterable & exportable',
                description: 'Filter by status, customize columns and export any report as a clean .csv — ready for your accountant, your books, or the QuickBooks sync you already run on.',
                content: (
                  <div className="flex h-full w-full items-center justify-center rounded-3xl bg-gray-50 p-6">
                    <div className="w-full max-w-sm rounded-2xl bg-white p-5" data-testid="money-story-export-visual">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-bold text-gray-900">Exports</span>
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-blue-700 bg-blue-50 rounded-full px-2.5 py-1"><Download className="w-3 h-3" /> .csv</span>
                      </div>
                      {[
                        ['trips_march.csv', 'Trips · Mar 1–31', true],
                        ['money_q1.csv', 'Money · Q1 range', true],
                        ['assets_ltm.csv', 'Assets · trailing 12mo', false],
                      ].map(([f, d, done]) => (
                        <div key={f} className="flex items-center gap-3 rounded-xl bg-gray-50 px-3.5 py-3 mb-2">
                          <FileSpreadsheet className="w-4 h-4 text-blue-600 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-gray-800 font-mono truncate">{f}</div>
                            <div className="text-[11px] text-gray-400">{d}</div>
                          </div>
                          {done ? <Check className="w-4 h-4 text-emerald-500 shrink-0" /> : <span className="text-[10px] font-bold text-blue-600 shrink-0">Compiling…</span>}
                        </div>
                      ))}
                      <p className="mt-2 text-[11px] text-gray-400 text-center">Ready for your accountant — or QuickBooks</p>
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      </section>

      {/* Concrete feature grid — replaces abstract "does more" cards with
          specific, sales-ready features operators actually compare on. */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Everything operators need
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
              {isAr ? <>الميزات التي يتقاضى منافسوك{" "}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">رسوماً إضافية مقابلها</span></> : <>The features your competitors{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">charge extra for</span></>}
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'كلها مشمولة. بلا رسوم إضافية. بلا رسائل ترقية إلى «الخطة الاحترافية».' : <>All included. No upsells. No "Pro plan" upgrade prompts.</>}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              // ── Revenue tools ────────────────────────────────────────────
              {
                icon: <BadgeDollarSign className="w-6 h-6" />,
                title: 'Gift Cards',
                desc: 'Depleting-balance gift cards with partial Stripe charges. Buyers and recipients get branded emails. Sell year-round, redeem instantly.',
                tint: 'from-violet-500 to-purple-600',
              },
              {
                icon: <TrendingUp className="w-6 h-6" />,
                title: 'Dynamic Pricing',
                desc: 'Surge weekends, discount slow days, tier by season — automatically. Pull more revenue from the same calendar.',
                tint: 'from-blue-500 to-cyan-600',
              },
              {
                icon: <Percent className="w-6 h-6" />,
                title: 'Discount Codes',
                desc: 'Reusable percentage or fixed-amount codes. Cap usage, set expiry, target specific services. Track redemption in real time.',
                tint: 'from-rose-500 to-pink-600',
              },
              // ── Financial control ────────────────────────────────────────
              {
                icon: <SlidersHorizontal className="w-6 h-6" />,
                title: 'Flexible Fee Control',
                desc: 'You decide who pays the fees. Operator absorbs, customer absorbs, or split. Add your own service fee to every booking with a toggle.',
                tint: 'from-emerald-500 to-teal-600',
                badge: 'New',
              },
              {
                icon: <Globe className="w-6 h-6" />,
                title: 'Multi-Currency',
                desc: 'USD, AED, EUR, GBP & more. Operators in the US, Med, UAE and beyond — each in their own currency and timezone.',
                tint: 'from-amber-500 to-orange-600',
              },
              {
                icon: <BarChart3 className="w-6 h-6" />,
                title: 'Per-Tenant Analytics',
                desc: 'GA4, Google Ads & Meta Pixel events fire under your own tenant IDs. Scale ad spend with confidence, not guesswork.',
                tint: 'from-cyan-500 to-sky-600',
              },
              // ── Operations ───────────────────────────────────────────────
              {
                icon: <Users className="w-6 h-6" />,
                title: 'Assignment on Confirmation',
                desc: 'Captains and guides claim trips from a shared dispatch queue. No more group-chat assignment chaos.',
                tint: 'from-indigo-500 to-blue-600',
              },
              {
                icon: <Zap className="w-6 h-6" />,
                title: 'No-Code Service Editor',
                desc: 'Spin up a new tour, edit pricing, or change a capacity in under a minute. Drag, drop, save — live for customers.',
                tint: 'from-pink-500 to-rose-600',
              },
              {
                icon: <Download className="w-6 h-6" />,
                title: 'Native iOS & Android Apps',
                desc: 'Your team books, dispatches and checks guests in from real native apps — not a wrapped browser tab.',
                tint: 'from-blue-600 to-indigo-700',
              },
              // ── Trust & growth ───────────────────────────────────────────
              {
                icon: <FileText className="w-6 h-6" />,
                title: 'Digital Waivers',
                desc: 'PIN-protected, device-fingerprinted, multi-signer ready. Legal audit trail captured automatically.',
                tint: 'from-slate-600 to-gray-700',
              },
              {
                icon: <Mail className="w-6 h-6" />,
                title: 'Automated Customer Comms',
                desc: 'SMS reminders, abandoned-cart recovery, thank-you emails, review nudges — sent on your behalf.',
                tint: 'from-sky-500 to-blue-600',
              },
              {
                icon: <Code className="w-6 h-6" />,
                title: 'Custom Websites Built For You',
                desc: 'Need more than a widget? We build a competitive, SEO-primed website tailored to your business — no templates.',
                tint: 'from-fuchsia-500 to-pink-600',
                badge: 'New',
              },
            ].map((f, i) => (
              <div
                key={i}
                className="group relative rounded-2xl bg-gray-50 p-6 hover:bg-gray-100/70 hover:-translate-y-0.5 transition-[background-color,transform] duration-200"
              >
                {f.badge && (
                  <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    {f.badge}
                  </span>
                )}
                <div className="w-11 h-11 rounded-xl bg-blue-600/10 text-blue-600 flex items-center justify-center mb-4">
                  {f.icon}
                </div>
                <h4 className="text-base font-semibold text-gray-900 mb-1.5 tracking-tight">{f.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Honest closer — operator-to-operator nod */}
          <div className="text-center mt-12">
            <p className="text-sm text-gray-400 italic">
              ...and a few hundred details we'll never put on a marketing page.
            </p>
          </div>
        </div>
      </section>



      {/* Ask Bosun — AI first mate. Character + instant help, guardrails up front. */}
      <section className="py-24 bg-white" data-testid="supplier-bosun-section">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1F2A5C]/5 mb-6">
              <Anchor className="w-3.5 h-3.5 text-[#1F2A5C]" strokeWidth={2.25} />
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#1F2A5C]">Meet Bosun · Included free</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-gray-900 mb-4" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'مساعدك الأول، دائماً في الخدمة.' : 'Your first mate, always on watch.'}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'كل لوحة تحكم في BookingFlows تأتي مع Bosun — دليل مدمج يعرف السفينة كلها. اسأل أين يوجد أي شيء، أو كيف تعمل ميزة، أو ماذا تُعِدّ تالياً، وسيدلّك Bosun على الطريق.' : <>Every BookingFlows dashboard ships with Bosun — a built-in guide who knows the whole vessel.
              Ask where anything lives, how a feature works, or what to set up next, and Bosun points the way.</>}
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Bosun render */}
            <div data-testid="supplier-bosun-chat-mock">
              <img
                src="/images/bf-bosun.webp"
                alt="Ask Bosun — BookingFlows' built-in first mate answering setup questions in chat"
                loading="lazy"
                className="w-full block"
              />
            </div>
            <p className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500 text-center" data-testid="bosun-trust-line">
              <Lock className="w-3.5 h-3.5 text-[#1F2A5C] shrink-0" />
              Bosun teaches the software only — zero access to your customer data, payments or credentials.
            </p>
          </div>
        </div>
      </section>

      {/* GetMyBoat sync — concise, hands-on "manual" concierge framing (intentionally
          understated; do NOT describe any automation). */}
      <section className="py-24 bg-white" data-testid="gmb-sync-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gray-50/60 px-6 py-8 sm:px-10 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
            <img src="/images/integrations/getmyboat-logo.png" alt="GetMyBoat" className="h-14 w-14 rounded-xl shadow-sm shrink-0" />
            <div className="flex-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">Concierge add-on</span>
              <h2 className="mt-1 text-2xl font-bold text-gray-900" dir={isAr ? 'rtl' : undefined}>{isAr ? 'موجود على GetMyBoat؟ تقويم واحد.' : 'Already on GetMyBoat? One calendar.'}</h2>
              <p className="mt-2 text-gray-600" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>فريقنا <span className="font-semibold text-gray-900">يراقب يدوياً</span> حجوزات GetMyBoat لديك ويبقيها متزامنة مع BookingFlows — لا حجوزات مزدوجة، ولا تنقّل بين النوافذ.</> : <>Our team <span className="font-semibold text-gray-900">manually monitors</span> your GetMyBoat bookings and keeps them in sync with BookingFlows — no double-bookings, no juggling tabs.</>}
              </p>
            </div>
            <button
              onClick={() => { trackSignupStarted('supplier_gmb_section'); navigate('/signup'); }}
              data-testid="supplier-gmb-signup"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-black transition-colors shrink-0">
              Get started
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>


      {/* Capacity cluster — strung together by a scroll-tracing beam */}
      <TracingBeam>
      {/* Customer Ticket Feature */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Link2 className="w-3.5 h-3.5" />
              Customer Experience
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
              {isAr ? <>رابط واحد،{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">كل ما يحتاجونه.</span></> : <>One link,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">everything they need.</span></>}
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'امنح عملاءك كل شيء بلمسة واحدة — بلا تحميل تطبيقات، وبلا حيرة.' : 'Give your customers everything in one tap — no app downloads, no confusion.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Features List */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-blue-500 p-3 rounded-lg flex-shrink-0">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">All Booking Details in One Place</h4>
                  <p className="text-gray-600">
                    Meeting location, time, asset name, guide info, and emergency contacts—everything your customer needs to show up prepared.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-500 p-3 rounded-lg flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">PIN-Protected Waiver Access</h4>
                  <p className="text-gray-600">
                    Customers sign waivers digitally using a secure PIN. Your team instantly knows who signed—no paperwork, no hassle.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-purple-500 p-3 rounded-lg flex-shrink-0">
                  <ArrowRight className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">Instantly Shareable</h4>
                  <p className="text-gray-600">
                    Send via text, email, or any messaging app. Works on any device—no app required. Customers get the link, they're ready to go.
                  </p>
                </div>
              </div>
            </div>

            {/* Ticket Mockup */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border-l-4 border-blue-600 p-8 max-w-md mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Booking ID</p>
                      <p className="font-mono font-bold text-blue-600">BF-1CWQ4T</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">Confirmed</span>
                </div>

                {/* Company Name */}
                <h3 className="font-bold text-2xl text-gray-900 mb-6">Austin ATV Rentals</h3>

                {/* Booking Details */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Asset</p>
                      <p className="font-semibold text-gray-900">Sunset Cruiser</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guide</p>
                      <p className="font-semibold text-gray-900">John S.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                      <p className="font-semibold text-gray-900">Sat, Nov 15</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                      <p className="font-semibold text-gray-900">2:00 PM</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                      <p className="font-semibold text-gray-900">3 hours</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                      <p className="font-semibold text-gray-900">8 people</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Meeting Location</p>
                    <p className="font-semibold text-gray-900">Trailhead Parking</p>
                  </div>
                </div>

                {/* Emergency */}
                <div className="bg-orange-50 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-orange-900 mb-1">Emergency Contact</p>
                  <p className="text-sm font-mono text-orange-700">+1 (512) 555-ASSET</p>
                </div>

                {/* Who's Signed */}
                <div className="bg-green-50 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-green-900 mb-2 uppercase tracking-wide">Who's Signed the Waiver</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">John Smith</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">Sarah Johnson</span>
                    </div>
                    <div className="flex items-center gap-2 opacity-50 mt-2 pt-2 border-t border-green-200">
                      <div className="w-4 h-4 bg-gray-200 rounded"></div>
                      <span className="text-sm text-gray-600">6 more guests need to sign</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <button className="btn-card-primary">
                    <Lock className="w-4 h-4" />
                    <span>Sign Waiver</span>
                    <span className="absolute right-4 text-xs opacity-75">2/8</span>
                  </button>
                  <button className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 fill-white" />
                    Leave a Review
                  </button>
                </div>
                
                {/* Footer - Copy Link */}
                <div className="text-center mt-6 pt-4 border-t">
                  <button className="text-sm text-gray-500 hover:text-gray-700 font-medium flex items-center justify-center gap-2 mx-auto">
                    <Link2 className="w-4 h-4" />
                    Copy Ticket Link
                  </button>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                Works on any device
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Text Content - Centered on top */}
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Globe className="w-3.5 h-3.5" />
              Expand Your Reach
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
              {isAr ? <>اعرض خدماتك على{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">سوقنا.</span></> : <>Get listed on our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">marketplace.</span></>}
            </h3>
            <p className="text-lg text-gray-500 mb-6 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'صِل إلى آلاف المسافرين الباحثين فعلياً عن تجارب مثل تجاربك.' : 'Reach thousands of travelers actively searching for experiences like yours.'}
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-8">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Instant visibility</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Professional profile</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">SEO-optimized</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Included free</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/marketplace')}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
              data-testid="supplier-view-marketplace"
            >
              View Marketplace
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          
          {/* Visual - Browser Window Mockup below */}
          <div className="relative max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-500">bookingflows.com</span>
                </div>
              </div>
              <div className="p-4 bg-gray-50">
                {/* Marketplace Header */}
                <div className="bg-white rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src="/logo-icon.png" alt="BookingFlows" className="w-6 h-6 rounded" />
                      <span className="font-semibold text-sm text-gray-900">BookingFlows</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-24 bg-gray-100 rounded-full"></div>
                    </div>
                  </div>
                </div>
                {/* Experience Cards Grid - Taller with photos */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg overflow-hidden">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1664532962042-07b2f58bdd8e?w=400')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">Sunset Boat Tour</p>
                      <p className="text-xs text-gray-500">Austin, TX</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$89</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.9</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg overflow-hidden">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('/marketplace-jetski.jpg')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">Jet Ski Rental</p>
                      <p className="text-xs text-gray-500">Lake Travis</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$120</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.8</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg overflow-hidden">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?w=400')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">Private Yacht Charter</p>
                      <p className="text-xs text-gray-500">Miami, FL</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$450</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.7</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-3 -right-3 bg-blue-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg">
              Free Listing
            </div>
          </div>
        </div>
      </section>

      {/* Done-for-you Websites — real proof (BOATX + Banana Boat sites we built) */}
      <section className="py-24 bg-white" data-testid="done-for-you-websites">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-fuchsia-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Done-for-you websites
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
              {isAr ? <>لا تملك موقعاً؟{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-pink-600">سنبني لك واحداً.</span></> : <>No website?{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-pink-600">We'll build you one.</span></>}
            </h3>
            <p className="text-lg text-gray-500 mb-6 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'منافس في محركات البحث، متكامل بالكامل، وخدماتك تعمل من اليوم الأول — بلا قوالب جاهزة وبلا رسوم شهرية للموقع. المواقع نفسها التي تحجز عملاء حقيقيين أدناه بُنيت على BookingFlows.' : 'SEO-competitive, fully integrated, and your services live from day one — no templates, no monthly website fees. The same sites that book real customers below were built on BookingFlows.'}
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
              {['SEO-optimized', 'Mobile-first', 'Bookings built in', 'Your brand, your domain'].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-fuchsia-600 flex-shrink-0" />
                  <span className="text-gray-700">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { name: 'Banana Boat Rentals', domain: 'bananaboatrentals.com', href: '/customers/banana-boat-rentals', cta: 'See the story',
                src: 'https://customer-assets.emergentagent.com/job_d269599a-400d-440c-92a3-9218d3ccd3af/artifacts/1tapvxni_Screenshot%202026-06-12%20at%209.40.35%E2%80%AFPM.png' },
              { name: 'Gee Transportation', domain: 'bookingflowsdemo.com', external: 'https://bookingflowsdemo.com', cta: 'Visit the site',
                src: 'https://customer-assets-0z36b82j.emergentagent.net/job_f27fe237-052e-4979-a423-fd4be621e1f9/artifacts/byeirckx_Screenshot%202026-08-02%20at%2012.56.43%E2%80%AFAM.webp' },
              { name: 'Charter Daze', domain: 'charterdaze.com', external: 'https://charterdaze.com', cta: 'Visit the site',
                src: 'https://customer-assets-0z36b82j.emergentagent.net/job_f27fe237-052e-4979-a423-fd4be621e1f9/artifacts/mi5x5o6u_Screenshot%202026-08-02%20at%201.00.31%E2%80%AFAM.webp' },
            ].map((site) => (
              <div key={site.name} className="group bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
                <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 flex items-center justify-center gap-1.5 text-xs text-gray-400">
                    <Lock className="w-3 h-3" /> {site.domain}
                  </div>
                </div>
                <div className="relative h-64 overflow-hidden bg-gray-50">
                  <img
                    src={proxyImg(site.src, 1000, 80)}
                    alt={`${site.name} website built by BookingFlows`}
                    loading="lazy"
                    decoding="async"
                    className="w-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
                <div className="flex items-center justify-between px-5 py-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{site.name}</div>
                    <div className="text-xs text-gray-400">Live on {site.domain}</div>
                  </div>
                  {site.external ? (
                    <a
                      href={site.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-fuchsia-600 hover:text-fuchsia-700 transition-colors"
                      data-testid={`dfy-story-${site.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {site.cta} <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <button
                      onClick={() => navigate(site.href)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-fuchsia-600 hover:text-fuchsia-700 transition-colors"
                      data-testid={`dfy-story-${site.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {site.cta} <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/demo')}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
              data-testid="dfy-book-demo"
            >
              Talk to us about a website
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>


      {/* CRM Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Users className="w-3.5 h-3.5" />
                Customer Management
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>كل حجز{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">يبني قاعدة عملائك.</span></> : <>Every booking{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">builds your CRM.</span></>}
              </h3>
              <p className="text-lg text-gray-500 mb-6 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'سجل الحجوزات، وبيانات التواصل، والتفضيلات — تُجمع كلها تلقائياً.' : 'Booking history, contact details, preferences — all collected automatically.'}
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Complete booking history per customer</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Contact details & preferences</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Automated thank you emails</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Export data anytime</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-gray-900">Recent Customers</h4>
                  <span className="text-sm text-blue-600 font-medium">View All</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">JS</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">John Smith</p>
                      <p className="text-sm text-gray-500">3 bookings • Last: Nov 15</p>
                    </div>
                    <span className="text-green-600 font-semibold">$450</span>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 font-semibold text-sm">SJ</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Sarah Johnson</p>
                      <p className="text-sm text-gray-500">5 bookings • Last: Nov 12</p>
                    </div>
                    <span className="text-green-600 font-semibold">$825</span>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-semibold text-sm">MD</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Mike Davis</p>
                      <p className="text-sm text-gray-500">1 booking • Last: Nov 10</p>
                    </div>
                    <span className="text-green-600 font-semibold">$150</span>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Total Customers</span>
                    <span className="font-semibold text-gray-900">1,234</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ⚡ Instant Lead Capture — embeddable inquiry form + instant team alerts */}
      <section className="py-24 bg-gray-50" data-testid="supplier-lead-capture-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Visual — white-label replica of the actual lead form + alert story */}
            <div className="order-2 lg:order-1">
              <div className="relative max-w-md mx-auto lg:mx-0 pt-6 pb-8" data-testid="lead-capture-visual">
                {/* The lead form (as it ships — white-label branded) */}
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                  <div className="h-1 bg-gray-100">
                    <div className="h-full w-1/4 bg-orange-500 rounded-r-full"></div>
                  </div>
                  <div className="p-6 sm:p-7">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-9 h-9 rounded-lg bg-gray-900 flex items-center justify-center text-white text-sm font-extrabold">YB</div>
                      <div>
                        <div className="text-sm font-extrabold text-gray-900 leading-tight">Your Brand Charters</div>
                        <div className="text-[11px] text-gray-400">Tell us what you're looking for — we'll get right back to you.</div>
                      </div>
                    </div>
                    <div className="text-[10px] font-bold tracking-[0.15em] text-orange-600 uppercase mb-1">Step 1 of 4</div>
                    <div className="text-lg font-extrabold text-gray-900 mb-4">How can we reach you?</div>
                    <div className="space-y-3">
                      <div>
                        <div className="text-[11px] font-bold text-gray-500 mb-1">Your name</div>
                        <div className="bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm text-gray-900">Jane Smith</div>
                      </div>
                      <div>
                        <div className="text-[11px] font-bold text-gray-500 mb-1">Email</div>
                        <div className="bg-orange-50/60 ring-2 ring-orange-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900">jane@example.com</div>
                      </div>
                      <div className="rounded-xl bg-gray-900 text-white text-center text-sm font-extrabold py-3">Continue →</div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 text-center text-[10px] text-gray-400">
                      🔒 Secure · Powered by <span className="font-bold text-gray-500">BookingFlows</span>
                    </div>
                  </div>
                </div>
                {/* Floating: instant crew alert */}
                <div className="absolute -top-1 -right-2 sm:-right-6 bg-gray-900 text-white rounded-xl shadow-xl px-4 py-3 rotate-2">
                  <div className="flex items-center gap-2 text-xs font-bold"><Zap className="w-3.5 h-3.5 text-orange-400" />Crew pinged</div>
                  <div className="text-[10px] font-mono text-gray-400 mt-0.5">email · SMS · WhatsApp · &lt;5 sec</div>
                </div>
                {/* Floating: the payoff */}
                <div className="absolute -bottom-1 -left-2 sm:-left-6 bg-white rounded-xl shadow-xl px-4 py-3 -rotate-2">
                  <div className="flex items-center gap-2 text-xs font-bold text-emerald-700"><Check className="w-3.5 h-3.5" />Booked &amp; paid</div>
                  <div className="text-[10px] font-mono text-gray-400 mt-0.5">straight onto your calendar</div>
                </div>
              </div>
            </div>
            {/* Copy */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 text-orange-600 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
                <Zap className="w-3.5 h-3.5" />
                New · Instant Lead Capture
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.12] mb-6" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>التقط كل عميل محتمل.{' '}<span className="text-orange-600">واكسب كل حجز.</span></> : <>Catch every lead.{' '}
                <span className="text-orange-600">Land every booking.</span></>}
              </h3>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'ضع نموذج استفسار سريعاً في أي صفحة من موقعك. لحظة إرساله، يصل تنبيه لطاقمك — ويمكن للعميل المحتمل أن ينتقل من «مجرد سؤال» إلى محجوز ومدفوع دون انتظار أي مكالمة.' : <>Put a quick inquiry form on any page of your website. The second it's submitted,
                your crew gets pinged — and the lead can go from "just asking" to booked-and-paid
                without ever waiting on a callback.</>}
              </p>
              <ul className="space-y-3 mb-10">
                {[
                  'Embeddable inquiry form — one line of code, styled to your brand',
                  'Instant alerts by email, text or WhatsApp — you pick who gets pinged, and when',
                  'Every lead lands in Relations with status, follow-ups and conversion tracking',
                  'Automatic follow-up offers — a discount code that reels in the fence-sitters',
                  'Plugs straight into Zapier — a lead.created event fires the moment a form comes in',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/signup')}
                className="group inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                data-testid="lead-capture-cta"
              >
                Start capturing leads
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* QR Scanner Section */}
      <section className="py-24 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <div className="flex gap-6 justify-center items-start">
                {/* Ticket with QR Code */}
                <div className="bg-white rounded-2xl shadow-xl p-4 w-48 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                    <div className="bg-blue-600 p-1.5 rounded">
                      <Calendar className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Booking ID</p>
                      <p className="text-xs font-bold text-blue-600">BF-ABC123</p>
                    </div>
                  </div>
                  <p className="font-bold text-sm text-gray-900 mb-1">BOATX</p>
                  <p className="text-xs text-gray-500 mb-3">Sat, Nov 29 • 2:00 PM</p>
                  {/* QR Code */}
                  <div className="bg-gray-50 p-3 rounded-xl mb-2">
                    <QrCode className="w-full h-auto text-gray-900" />
                  </div>
                  <p className="text-[10px] text-center text-gray-400">Scan to check in</p>
                </div>

                {/* Phone Scanner */}
                <div className="relative max-w-[200px]">
                  <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl">
                    <div className="bg-white rounded-[1.5rem] overflow-hidden">
                      <div className="bg-purple-600 px-4 py-2">
                        <p className="text-white font-semibold text-sm text-center">Scan QR Code</p>
                      </div>
                      <div className="p-3">
                        {/* Scanner Viewfinder with QR */}
                        <div className="relative aspect-square bg-gray-900 rounded-xl mb-3 overflow-hidden">
                          {/* Corner brackets */}
                          <div className="absolute top-2 left-2 w-4 h-4 border-t border-l border-purple-400"></div>
                          <div className="absolute top-2 right-2 w-4 h-4 border-t border-r border-purple-400"></div>
                          <div className="absolute bottom-2 left-2 w-4 h-4 border-b border-l border-purple-400"></div>
                          <div className="absolute bottom-2 right-2 w-4 h-4 border-b border-r border-purple-400"></div>
                          {/* Scan line */}
                          <div className="absolute left-2 right-2 h-0.5 bg-purple-500 top-1/3 animate-pulse shadow-[0_0_10px_rgba(168,85,247,0.8)]"></div>
                          {/* QR Code being scanned */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 bg-white p-1.5 rounded shadow-lg">
                              <QrCode className="w-full h-full text-gray-900" />
                            </div>
                          </div>
                        </div>
                        {/* Success State */}
                        <div className="bg-green-50 rounded-lg p-2 text-center">
                          <Check className="w-5 h-5 text-green-600 mx-auto mb-1" />
                          <p className="font-semibold text-green-800 text-xs">Verified!</p>
                          <p className="text-[10px] text-green-600">John Smith • 4 guests</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <QrCode className="w-3.5 h-3.5" />
                QR Check-In
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>حوّل أي جهاز{' '}<span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">إلى ماسح ضوئي.</span></> : <>Turn any device{' '}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">into a scanner.</span></>}
              </h3>
              <p className="text-gray-600 text-lg mb-6" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'لا حاجة لأجهزة خاصة. هاتفك يصبح نظام تسجيل وصول احترافياً. امسح رموز QR الخاصة بالعملاء وتحقق من الحجوزات فوراً.' : 'No special hardware needed. Your phone becomes a professional check-in system. Scan customer QR codes and verify bookings instantly.'}
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Works on any smartphone or tablet</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Instant guest verification</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">See booking details immediately</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Track check-ins in real-time</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* White Label Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                Your Brand
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>اجعله بعلامتك{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">التجارية الخاصة.</span></> : <>White-label it{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">as your own.</span></>}
              </h3>
              <p className="text-gray-600 text-lg mb-6" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'اجعله ملكك. أزل علامتنا التجارية تماماً وقدّم نظام الحجز كأنه نظامك أنت. عملاؤك يرون علامتك، لا علامتنا.' : 'Make it yours. Remove our branding completely and present the booking system as your own. Your customers see your brand, not ours.'}
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">Your logo on all customer touchpoints</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">Custom colors to match your brand</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">No "Powered by" watermarks</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">100% free—included with your account</span>
                </li>
              </ul>
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-green-800 font-medium">
                  Free of charge. On us. No hidden fees.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="space-y-4">
                {/* Before */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-2 font-medium">BEFORE</p>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-lg"></div>
                      <span className="font-semibold text-gray-400">BookingFlows</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                    <div className="mt-3 pt-3 border-t text-xs text-gray-400 text-center">
                      Powered by BookingFlows
                    </div>
                  </div>
                </div>
                {/* After */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-xs text-blue-600 mb-2 font-medium">AFTER (WHITE LABEL)</p>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">YB</div>
                      <span className="font-semibold text-gray-900">Your Brand</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                    <div className="mt-3 pt-3 border-t text-xs text-gray-900 text-center font-medium">
                      © Your Company Name
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      </TracingBeam>

      {/* Payments & Payouts — Stripe + Ziina, flat navy */}
      <section className="py-24 bg-[#04091B] text-white" data-testid="supplier-payments-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-blue-300 text-xs font-semibold uppercase tracking-[0.2em] mb-6" dir={isAr ? 'rtl' : undefined}>
              <DollarSign className="w-3.5 h-3.5" />
              {isAr ? 'المدفوعات والتحويلات' : <>Payments &amp; Payouts</>}
            </div>
            <div className="flex items-center justify-center gap-3 mb-7">
              <div className="bg-white rounded-2xl p-3">
                <svg className="w-16 h-7" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M60 12.8c0-4.28-2.07-7.66-6.03-7.66-3.98 0-6.38 3.38-6.38 7.63 0 5.04 2.84 7.58 6.92 7.58 1.99 0 3.49-.45 4.63-1.09v-3.33c-1.14.57-2.45.93-4.11.93-1.63 0-3.07-.57-3.25-2.56h8.19c0-.22.03-1.08.03-1.5zm-8.19-1.59c0-1.88 1.15-2.67 2.19-2.67 1.02 0 2.1.79 2.1 2.67h-4.29zM41.3 5.14c-1.64 0-2.7.77-3.28 1.3l-.22-1.04H34v19.8l4.22-.9v-4.8c.6.44 1.49 1.06 2.97 1.06 3 0 5.74-2.42 5.74-7.77 0-4.9-2.78-7.65-5.63-7.65zm-.99 11.76c-.99 0-1.57-.36-1.98-.8v-6.3c.44-.48 1.04-.82 1.98-.82 1.52 0 2.57 1.7 2.57 3.94 0 2.3-1.03 3.98-2.57 3.98zM27.65 4.3l4.24-.9V0l-4.24.9v3.4zm0 .98h4.24v14.6h-4.24V5.28zM22.94 6.6l-.26-1.32h-3.63v14.6h4.22v-9.9c1-.3 2.68-1.06 3.2-.88V5.28c-.54-.2-2.51-.57-3.53 1.32zM13.68 1.38L9.56 2.25v13.35c0 2.47 1.85 4.28 4.3 4.28 1.36 0 2.35-.25 2.9-.55v-3.45c-.53.22-3.17 1-3.17-1.5V8.93h3.17V5.28h-3.17l.09-3.9zM4.16 9.84c0-.65.54-.9 1.42-.9 1.27 0 2.87.39 4.14 1.08V6.2c-1.39-.55-2.76-.77-4.14-.77C2.25 5.43 0 7.1 0 10.02c0 4.5 6.2 3.78 6.2 5.72 0 .77-.67 1.02-1.6 1.02-1.39 0-3.17-.57-4.58-1.35v3.82c1.56.67 3.14.96 4.58.96 3.35 0 5.58-1.66 5.58-4.72 0-4.86-6.22-4-6.22-5.63z" fill="#635BFF"/>
                </svg>
              </div>
              <div className="bg-white rounded-2xl p-3 flex items-center h-[52px]" data-testid="landing-ziina-logo">
                <img src="/img/ziina-lockup.png" alt="Ziina" className="h-7 w-auto" loading="lazy" />
              </div>
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 leading-[1.1]" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'مدفوعات مدعومة من Stripe وZiina' : <>Payments powered by Stripe &amp; Ziina</>}
            </h3>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'مدفوعات آمنة، وتحويلات فورية، ودعم متعدد العملات — عبر Stripe عالمياً وZiina، معالج الدفع المخصص لعملائنا في الإمارات.' : 'Secure payments, instant payouts, and multi-currency support — Stripe worldwide, plus Ziina, our dedicated payment processor for UAE operators.'}
            </p>
          </div>

          {/* Feature tiles — flat */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mb-10">
            {[
              { icon: Shield, tint: 'bg-emerald-500/15 text-emerald-300', t: 'Secure Payments', d: 'Bank-level encryption for every transaction' },
              { icon: Zap, tint: 'bg-blue-500/15 text-blue-300', t: 'Instant Payouts', d: 'Money in your account within days' },
              { icon: Globe, tint: 'bg-sky-500/15 text-sky-300', t: 'Multi-Currency', d: 'USD, AED, EUR, GBP and more at checkout' },
              { icon: FileText, tint: 'bg-amber-500/15 text-amber-300', t: 'Tax Forms (1099)', d: 'Automated tax reporting handled for you' },
            ].map((f) => (
              <div key={f.t} className="rounded-2xl bg-white/[0.06] p-5">
                <div className={'w-10 h-10 rounded-xl flex items-center justify-center mb-3 ' + f.tint}>
                  <f.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h4 className="font-semibold mb-1">{f.t}</h4>
                <p className="text-sm text-slate-400">{f.d}</p>
              </div>
            ))}
          </div>

          {/* Ziina — dedicated UAE payment rail */}
          <div className="bg-white rounded-2xl p-6 sm:p-7 mb-10 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="landing-ziina-strip" dir={isAr ? 'rtl' : undefined}>
            <img src="/img/ziina-lockup.png" alt="Ziina" className="h-9 w-auto shrink-0" loading="lazy" />
            <div className="flex-1">
              <h4 className="text-gray-900 font-semibold text-base mb-1">
                {isAr ? 'الآن مع Ziina — معالج دفع مخصص لدولة الإمارات' : 'Now with Ziina — a dedicated UAE payment processor'}
              </h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                {isAr
                  ? 'يستقبل المشغّلون في الإمارات مدفوعاتهم بالدرهم مباشرة عبر Ziina المرخّصة من مصرف الإمارات المركزي — بنفس تجربة الحجز الفورية والاستردادات والإيصالات.'
                  : 'UAE operators charge in AED natively through Ziina — licensed by the Central Bank of the UAE — with the same instant booking flows, refunds and branded receipts.'}
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs font-medium text-gray-600 shrink-0">
              <Shield className="w-4 h-4 text-blue-600" />
              <span>{isAr ? 'مرخّصة من مصرف الإمارات المركزي' : 'Licensed by the Central Bank of the UAE'}</span>
            </div>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 text-center text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>PCI Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>256-bit SSL</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Millions of Businesses Trust Stripe</span>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section - More Software-y */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Link2 className="w-3.5 h-3.5" />
              Integrations
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
              {isAr ? <>نتكامل مع{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">قنواتك.</span></> : <>We integrate with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">your channels.</span></>}
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'زامن توافرك وحجوزاتك عبر المنصات تلقائياً.' : 'Sync your availability and bookings across platforms automatically.'}
            </p>
          </div>

          {/* Integration Visual */}
          <div className="max-w-4xl mx-auto">
            <div className="relative flex items-center justify-center gap-2 sm:gap-4 md:gap-8">
              {/* Left Platform - GetYourGuide */}
              <div className="flex flex-col items-center min-w-0">
                <div className="bg-white rounded-2xl p-2.5 sm:p-4 shadow-lg hover:shadow-xl transition-all relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full">LIVE</div>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl overflow-hidden">
                    <img src="/images/integrations/gyg-icon.jpg" alt="GetYourGuide" className="w-full h-full object-cover" />
                  </div>
                </div>
                <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700">GetYourGuide</span>
              </div>

              {/* Connection Line with Arrow */}
              <div className="flex items-center flex-shrink-0">
                <div className="w-3 sm:w-6 md:w-12 h-0.5 bg-gradient-to-r from-green-400 to-blue-400"></div>
                <div className="bg-blue-500 rounded-full p-1">
                  <ArrowRight className="w-3 h-3 text-white" />
                </div>
              </div>

              {/* Center - BookingFlows */}
              <div className="flex flex-col items-center min-w-0">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-2.5 sm:p-4 shadow-xl relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full">SYNC</div>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                    <img src="/logo-icon.png" alt="BookingFlows" className="w-9 h-9 sm:w-12 sm:h-12 md:w-16 md:h-16 object-contain" />
                  </div>
                </div>
                <span className="mt-2 text-xs sm:text-sm font-bold text-blue-600">BookingFlows</span>
              </div>

              {/* Connection Line with Arrow */}
              <div className="flex items-center flex-shrink-0">
                <div className="bg-blue-500 rounded-full p-1">
                  <ArrowRight className="w-3 h-3 text-white rotate-180" />
                </div>
                <div className="w-3 sm:w-6 md:w-12 h-0.5 bg-gradient-to-l from-green-400 to-blue-400"></div>
              </div>

              {/* Right Platform - LetsBatch */}
              <div className="flex flex-col items-center min-w-0">
                <div className="bg-white rounded-2xl p-2.5 sm:p-4 shadow-lg hover:shadow-xl transition-all relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full">LIVE</div>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-black p-1 flex items-center justify-center">
                    <img src="/images/integrations/letsbatch-icon.png" alt="LetsBatch" className="w-full h-full object-contain" />
                  </div>
                </div>
                <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700">LetsBatch</span>
              </div>
            </div>

            {/* Coming Soon - Viator */}
            <div className="flex justify-center mt-8">
              <div className="flex items-center gap-3 bg-gray-50 rounded-full px-5 py-2.5">
                <div className="w-8 h-8 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                  <img src="/images/integrations/viator-icon.png" alt="Viator" className="w-full h-full object-contain" />
                </div>
                <span className="text-sm text-gray-600">Viator</span>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Coming Soon</span>
              </div>
            </div>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <div className="bg-white rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">Real-time availability sync</span>
              </div>
              <div className="bg-white rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">Automatic booking updates</span>
              </div>
              <div className="bg-white rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">No double bookings</span>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* ───────────────────────────────────────────────────────────────
          Operations Timeline / Audit Trail — every action recorded.
          Accountability story that flows straight into the Security section.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white animate-on-scroll overflow-hidden" data-testid="supplier-audit-trail-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left — copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Clock className="w-3.5 h-3.5" />
                The Log book
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-6" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'كل إجراء، مسجّل.' : 'Every action, recorded.'}
              </h3>
              <p className="text-xl text-gray-600 leading-relaxed mb-8" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'سجل تدقيق كامل عبر عمليتك كلها — طاقمك يعرفه باسم «دفتر السجل». من حرّك حجزاً، ومن وافق على استرداد، ومن سجّل وصول ضيف — بطابع زمني ونسبة واضحة، وموجود دائماً عند حاجتك.' : 'A full audit trail across your whole operation — your crew knows it as the Log book. Who moved a booking, who approved a refund, who checked a guest in — timestamped, attributed, and always there when you need it.'}
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Every edit, refund, and reassignment attributed to a team member</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Waivers, payments, and check-ins stamped the second they happen</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Accountability that protects you in disputes and chargebacks</span>
                </li>
              </ul>
            </div>

            {/* Right — live audit feed mock */}
            <div className="relative">
              <div className="absolute -top-10 -right-8 w-64 h-64 rounded-full bg-blue-100/60 blur-3xl pointer-events-none" />
              <div className="relative bg-white rounded-2xl shadow-[0_25px_60px_-25px_rgba(15,23,42,0.25)] overflow-hidden" data-testid="supplier-audit-trail-feed">
                {/* card header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-gray-50/70">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-800">Log book</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-600">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    Live
                  </span>
                </div>
                {/* entries */}
                <div className="relative px-5 py-4">
                  <div className="absolute left-[38px] top-8 bottom-8 w-px bg-gray-100" />
                  {[
                    { time: '9:42 AM', icon: Users, tint: 'bg-blue-50 text-blue-600', text: <>Ryan reassigned <span className="font-semibold text-gray-900">Booking BK-2194</span> to Captain John</> },
                    { time: '9:45 AM', icon: FileText, tint: 'bg-purple-50 text-purple-600', text: <>Customer <span className="font-semibold text-gray-900">signed waiver</span></> },
                    { time: '9:47 AM', icon: DollarSign, tint: 'bg-emerald-50 text-emerald-600', text: <>Payment <span className="font-semibold text-gray-900">captured</span></> },
                    { time: '10:01 AM', icon: Check, tint: 'bg-teal-50 text-teal-600', text: <>Booking <span className="font-semibold text-gray-900">checked in</span></> },
                    { time: '10:04 AM', icon: Bell, tint: 'bg-amber-50 text-amber-600', text: <>SMS confirmation <span className="font-semibold text-gray-900">delivered</span></> },
                    { time: '10:10 AM', icon: ArrowLeftRight, tint: 'bg-rose-50 text-rose-600', text: <>Refund <span className="font-semibold text-gray-900">approved by Manager</span></> },
                  ].map((e, i) => (
                    <div key={i} className="relative flex items-start gap-3.5 py-2.5">
                      <div className={`relative z-10 w-9 h-9 rounded-full ${e.tint} flex items-center justify-center shrink-0 ring-4 ring-white`}>
                        <e.icon className="w-4 h-4" />
                      </div>
                      <div className="pt-0.5 min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">{e.time}</div>
                        <div className="text-sm text-gray-600 leading-snug">{e.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-blue-400 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Shield className="w-3.5 h-3.5" />
              Enterprise-Grade Security
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1] mb-6" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'بياناتك، محمية.' : 'Your data, protected.'}
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'صُمم لأعمال تأخذ الخصوصية على محمل الجد. نعمل نحو أعلى معايير الامتثال.' : "Built for businesses that take privacy seriously. We're working toward the highest compliance standards."}
            </p>
          </div>

          {/* Security Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Encryption */}
            <div className="bg-white/[0.07] backdrop-blur-sm rounded-2xl p-8 transition-colors hover:bg-white/10">
              <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center mb-6">
                <Lock className="w-7 h-7 text-green-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">Encrypted at Rest &amp; in Transit</h4>
              <p className="text-gray-400 leading-relaxed">
                <span className="text-green-400 font-medium">AES-256 encryption at rest</span> and <span className="text-green-400 font-medium">TLS 1.3 in transit</span>. Your customer data, payment info, and signed waivers are protected at every step — from browser to database.
              </p>
            </div>

            {/* EU Hosting */}
            <div className="bg-white/[0.07] backdrop-blur-sm rounded-2xl p-8 transition-colors hover:bg-white/10">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-blue-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">EU Data Residency</h4>
              <p className="text-gray-400 leading-relaxed">
                Primary databases and APIs hosted in <span className="text-blue-400 font-medium">Frankfurt, Germany</span>—one of the world's strictest data privacy jurisdictions.
              </p>
            </div>

            {/* Compliance */}
            <div className="bg-white/[0.07] backdrop-blur-sm rounded-2xl p-8 transition-colors hover:bg-white/10">
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6">
                <FileText className="w-7 h-7 text-purple-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">Compliance-Ready</h4>
              <p className="text-gray-400 leading-relaxed">
                Building toward <span className="text-purple-400 font-medium">GDPR</span>, <span className="text-purple-400 font-medium">CCPA</span>, and <span className="text-purple-400 font-medium">SOC 2</span> compliance standards. We take privacy regulations seriously.
              </p>
            </div>
          </div>

          {/* Everything-you'd-expect checklist */}
          <div className="mb-16" data-testid="supplier-security-checklist">
            <p className="text-center text-[11px] uppercase tracking-[0.2em] text-gray-500 font-semibold mb-6">
              Everything you'd expect — already on
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                'Daily encrypted backups',
                'Cloudflare edge protection',
                'Stripe PCI-compliant payments',
                'AWS object storage',
                'Role-based permissions',
                'FIDO2 keys, passkeys & 2FA',
                'Audit logs',
                'Automatic SSL',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5 bg-white/[0.07] rounded-xl px-4 py-3">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-green-400" />
                  </div>
                  <span className="text-sm text-gray-200">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Security roadmap — honest "what's next" builds more trust than claiming perfection */}
          <div className="mb-16 text-center" data-testid="supplier-security-roadmap">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-semibold mb-4">
              On the security roadmap
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['SOC 2 certification', 'Independent penetration testing', 'Bug bounty program'].map((item) => (
                <span key={item} className="inline-flex items-center gap-2 bg-white/[0.07] rounded-full px-4 py-2 text-sm text-gray-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  {item}
                </span>
              ))}
            </div>
            <button
              onClick={() => navigate('/security')}
              data-testid="supplier-security-page-link"
              className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors"
            >
              See our full security posture
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>

          {/* Reliability stat band */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 pt-10 border-t border-white/10 text-center" data-testid="supplier-reliability-stats">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">99.9%</div>
              <div className="text-sm text-gray-400">Uptime target</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">24/7</div>
              <div className="text-sm text-gray-400">Continuous monitoring &amp; alerts</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">Daily</div>
              <div className="text-sm text-gray-400">Encrypted backups, tested recovery</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">Global</div>
              <div className="text-sm text-gray-400">CDN + autoscaling infrastructure</div>
            </div>
          </div>
        </div>
      </section>

      {/* 🔑 Enterprise Hardware Keys — physical FIDO2 keys, programmed & shipped
          by us for larger operators. Sits directly under the dark security
          section so the story reads: platform security → hardware upgrade. */}
      <section className="py-16 bg-white" data-testid="supplier-hardware-keys-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center grayscale opacity-60 transition-[opacity,filter] duration-500 hover:grayscale-0 hover:opacity-100">
            {/* Visual — left on desktop */}
            <div className="relative order-1">
              <img
                src="/images/bf-hardware-keys.webp"
                alt="A FIDO2 hardware security key protecting a BookingFlows organization — key, shield and dashboard connected"
                loading="lazy"
                data-testid="hardware-keys-section-visual"
                className="w-full block"
              />
              <div className="absolute -bottom-4 left-6 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white shadow-md">
                <span className="w-5 h-5 rounded-md bg-emerald-500 text-white flex items-center justify-center">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                <span className="text-xs font-semibold text-gray-700">Arrives registered &amp; ready to tap</span>
              </div>
            </div>

            {/* Copy — right on desktop */}
            <div className="order-2">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gray-100 mb-6">
                <Key className="w-3.5 h-3.5 text-gray-500" strokeWidth={2.25} />
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">Optional · Enterprise Hardware Keys</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>التصيّد لا يمكنه لمس{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">مفتاح مادي.</span></> : <>Phishing can&apos;t touch{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  a physical key.
                </span></>}
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>للمشغّلين الكبار، نبرمج ونشحن <strong className="text-gray-800">مفاتيح أمان مادية معتمدة FIDO2</strong> مهيّأة مسبقاً لفريقك — يصل كل مفتاح مسجّلاً بالفعل للحساب الصحيح. وزّعها، والمس للدخول، وانتهى. حتى صفحة تسجيل دخول مزيفة متقنة تماماً لا تستطيع التقاط مفتاح مادي.</> : <>For larger operators, we program and ship <strong className="text-gray-800">FIDO2-certified
                hardware security keys</strong> pre-configured for your team — each key arrives already
                registered to the right account. Hand them out, tap to sign in, done. Even a
                pixel-perfect fake login page can&apos;t capture a hardware key.</>}
              </p>
              <div className="space-y-3 mb-8">
                {[
                  { icon: Package, text: 'Programmed by our engineers and shipped ready to use — zero setup for your team' },
                  { icon: Fingerprint, text: 'FIDO2 / WebAuthn certified — the same phishing-resistant standard we use internally' },
                  { icon: Users, text: 'Pairs with one-toggle org-wide 2FA, so every seat is covered from day one' },
                ].map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <line.icon className="w-4 h-4" strokeWidth={2} />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700 pt-1.5">{line.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Button
                  onClick={() => navigate('/support?category=Hardware%20Security%20Keys#tickets')}
                  data-testid="hardware-keys-section-cta"
                  className="group bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-7 h-12 text-sm font-semibold shadow-[0_4px_14px_rgba(38,99,235,0.3)] transition-all"
                >
                  Talk to us about hardware keys
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
                <span className="text-xs text-gray-500">Fleet-sized orders · backup keys included</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crypto Payments — light, compact card-style. Was a heavy dark section
          that double-stacked with Stripe + Security + final CTA. */}
      <section className="py-24 bg-white animate-on-scroll">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center bg-gradient-to-br from-indigo-50 via-white to-blue-50 rounded-3xl p-8 sm:p-12">
            {/* Left: Copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <Bitcoin className="w-3.5 h-3.5" />
                Crypto Payments
              </div>
              <h3 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-4 leading-[1.1]" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>اقبل العملات الرقمية.{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">واستلم دولارات.</span></> : <>Accept crypto.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Get paid in dollars.</span></>}
              </h3>
              <p className="text-base text-gray-600 mb-6 leading-relaxed" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'يدعم BookingFlows مدفوعات عملة USDC المستقرة عبر Stripe. يدفع العملاء بالعملات الرقمية — وتستلم أنت دولاراً أمريكياً بلا أي مخاطر تقلب. Stripe يتولى التحويل والامتثال.' : <>BookingFlows supports USDC stablecoin payments via Stripe. Customers pay with crypto —
                you receive USD with zero volatility risk. Stripe handles conversion and compliance.</>}
              </p>
              <div className="space-y-2.5">
                {[
                  'Customers pay with USDC at checkout',
                  'You receive USD — no crypto knowledge needed',
                  'Works alongside card, Apple Pay, Google Pay',
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: payment methods card */}
            <div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400 font-semibold mb-4">At Checkout</p>
                <div className="space-y-2.5">
                  {[
                    { label: 'Credit / Debit Card', sub: 'Visa, Mastercard, Amex' },
                    { label: 'Apple Pay / Google Pay', sub: 'One-tap mobile payment' },
                    { label: 'Crypto (USDC)', sub: 'Stablecoin · Settles as USD', badge: 'New' },
                    { label: 'Buy Now, Pay Later', sub: 'Klarna, Affirm' },
                  ].map((m, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50/50">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">{m.label}</p>
                          {m.badge && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                              {m.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{m.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-20 -left-28 w-80 h-80 bg-blue-100/40 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-24 -right-24 w-72 h-72 bg-slate-200/30 rounded-full blur-3xl -z-10" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4" dir={isAr ? 'rtl' : undefined}>
              <BadgeDollarSign className="w-3.5 h-3.5" />
              {isAr ? 'أسعار بسيطة' : 'Simple Pricing'}
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4" dir={isAr ? 'rtl' : undefined}>
              {isAr ? <>لا نربح إلا{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">عندما تربح أنت.</span></> : <>We only make money{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">when you do.</span></>}
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" dir={isAr ? 'rtl' : undefined}>
              {isAr
                ? 'بدون رسوم شهرية. بدون تكاليف إعداد. فقط رسم بسيط يُضاف إلى كل حجز — يدفعه عميلك، وليس أنت.'
                : 'No monthly fees. No setup costs. Just a small fee added to each booking—paid by your customer, not you.'}
            </p>
          </div>

          <div className="max-w-sm mx-auto">
            {/* Pricing Breakdown Card */}
            <div className="relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-900 text-white text-[11px] font-bold shadow-lg whitespace-nowrap">
              <FileText className="w-3 h-3" /> {isAr ? 'مثال على حجز' : 'Example booking'}
            </div>
            <Card className="bg-white border-0 ring-1 ring-slate-900/[0.06] shadow-[0_30px_60px_-25px_rgba(15,23,42,0.3)] rounded-3xl overflow-hidden">
              <CardContent className="p-0">
                {/* Receipt Style Breakdown */}
                <div className="px-7 pt-9 pb-6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">{isAr ? 'الخدمة' : 'Service'}</span>
                      <span className="font-semibold text-gray-900">{isAr ? 'AED 1,300.00' : '$350.00'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">{isAr ? 'الإضافات' : 'Add-ons'}</span>
                      <span className="font-semibold text-gray-900">{isAr ? '+AED 200.00' : '+$50.00'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">{isAr ? 'ضريبة القيمة المضافة (5%)' : 'Tax'}</span>
                      <span className="font-semibold text-gray-900">{isAr ? '+AED 75.00' : '+$33.00'}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-dashed border-gray-200">
                      <span className="inline-flex items-center gap-2 text-gray-500">
                        BookingFlows
                        <span className="px-1.5 py-0.5 rounded-md bg-[#EBF2FF] text-[#2563EB] text-[10px] font-bold">3%</span>
                      </span>
                      <span className="font-semibold text-[#2563EB]">+$13.00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">{isAr ? 'رسوم معالجة Stripe*' : 'Stripe processing*'}</span>
                      <span className="font-semibold text-gray-400">{isAr ? '+AED 49.48' : '+$13.32'}</span>
                    </div>
                  </div>
                  {/* Total */}
                  <div className="mt-5 pt-4 border-t-2 border-slate-900">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-900">{isAr ? 'يدفع العميل' : 'Customer pays'}</span>
                      <span className="font-extrabold text-gray-900 text-xl">{isAr ? 'AED 1,671.73' : '$459.32'}</span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1" dir={isAr ? 'rtl' : undefined}>{isAr ? 'الرسوم تُضاف فوق السعر — يدفعها عميلك، لا أنت.' : 'Fees are added on top — paid by your customer, not you.'}</p>
                  </div>
                  {/* You keep */}
                  <div className="mt-5 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 ring-1 ring-emerald-500/20 p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-1.5 font-bold text-emerald-800">
                          <Check className="w-4 h-4" strokeWidth={3} /> {isAr ? 'أنت تحتفظ بـ' : 'You keep'}
                        </div>
                        <p className="text-[11px] text-emerald-600/80 mt-0.5" dir={isAr ? 'rtl' : undefined}>{isAr ? 'الخدمة + الإضافات + الضريبة — حتى آخر فلس' : 'Service + add-ons + tax — every cent'}</p>
                      </div>
                      <span className="font-extrabold text-emerald-700 text-2xl">{isAr ? 'AED 1,575.00' : '$433.00'}</span>
                    </div>
                  </div>
                  {/* Stripe footnote */}
                  <p className="text-[11px] text-gray-400 mt-4" dir={isAr ? 'rtl' : undefined}>
                    {isAr ? '‏*يتقاضى Stripe ‏2.9% + رسماً ثابتاً لكل معاملة — يُدفع مباشرة إلى Stripe، لا إلينا أبداً.' : <>*Stripe charges 2.9% + 30&cent; per transaction &mdash; paid straight to Stripe, never to us.</>}
                  </p>
                </div>
                {/* CTA */}
                <div className="px-7 pb-7">
                  <Button 
                    className="btn-supplier-primary w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 rounded-full"
                    onClick={() => { trackSignupStarted('supplier_pricing'); navigate('/signup'); }}
                    data-testid="supplier-pricing-cta"
                  >
                    {isAr ? 'ابدأ الآن' : 'Get Started'} <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            </div>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              {isAr ? 'بدون رسوم شهرية • بدون تكاليف إعداد • ألغِ في أي وقت' : 'No monthly fees • No setup costs • Cancel anytime'}
            </p>
          </div>

          {/* Referral perk — moved from its own dark section into pricing. */}
          <div className="max-w-3xl mx-auto mt-14" data-testid="supplier-referral-section">
            <div className="rounded-3xl bg-emerald-50 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
              <div className="flex-1 text-center sm:text-left" dir={isAr ? 'rtl' : undefined}>
                <div className="inline-flex items-center gap-1.5 text-emerald-700 text-[11px] font-bold uppercase tracking-[0.18em] mb-2">
                  <Handshake className="w-3.5 h-3.5" /> {isAr ? 'برنامج الإحالة' : 'Referral program'}
                </div>
                <h4 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-1.5">
                  {isAr ? <>تعرف مشغّلاً؟ <span className="text-emerald-600">اربح حتى 40%.</span></> : <>Know an operator? <span className="text-emerald-600">Earn up to 40%.</span></>}
                </h4>
                <p className="text-sm text-gray-600">
                  {isAr
                    ? 'شارك في أرباحنا من كل شركة تحيلها طوال عامها الأول — لست بحاجة حتى أن تكون عميلاً.'
                    : "Share in the profit we make from every company you refer, for their entire first year — you don't even need to be a customer."}
                </p>
                <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-4" data-testid="referral-teaser-tiers">
                  {[["1st","10%"],["2nd","20%"],["3rd","30%"],["4th+","40%"]].map(([n,pct]) => (
                    <div key={n} className="flex items-baseline gap-1.5 rounded-xl bg-white px-3 py-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-emerald-600/80">{n}</span>
                      <span className="text-sm font-bold text-gray-900">{pct}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2.5 w-full sm:w-auto">
                <button
                  onClick={() => navigate('/referral-program')}
                  data-testid="supplier-referral-cta"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors"
                >
                  {isAr ? 'ابدأ الربح' : 'Start earning'} <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate('/blog/referral-program')}
                  data-testid="supplier-referral-learn"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-white text-gray-700 text-sm font-semibold hover:bg-gray-100 transition-colors"
                >
                  {isAr ? 'كيف يعمل' : 'How it works'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom feature commitment — sincere, human, low-pressure. Positions
          BookingFlows as a real engineering partner rather than rigid SaaS.
          Sits right before the final sign-up CTA so the last impression is
          "they'll actually listen to me." */}
      <section className="py-24 bg-gradient-to-b from-white via-blue-50/50 to-white relative overflow-hidden animate-on-scroll">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-blue-200/25 rounded-full blur-3xl -z-0" aria-hidden="true" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-indigo-200/25 rounded-full blur-3xl -z-0" aria-hidden="true" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
            {/* Copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <Sparkles className="w-3.5 h-3.5" />
                We Build What You Need
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5">
                Need something custom?{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Just ask.
                </span>
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7">
                Every operator runs their business a little differently — and BookingFlows
                is built by an operator who gets that. If you need a workflow, integration,
                report, or feature that doesn't exist yet, tell us. We genuinely consider
                every request and ship the ones that make your business better.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  'Real engineers read every request — it goes to builders, not a black hole',
                  'Roadmap shaped by operator feedback, not investor decks',
                  'New feature shipped? Available to every customer, free.',
                ].map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">{line}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/support?category=Feature%20request#tickets')}
                  className="group inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
                  data-testid="supplier-custom-feature-cta"
                >
                  Submit a feature request
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/support')}
                  className="text-sm text-gray-600 hover:text-gray-900 underline-offset-4 hover:underline transition-colors"
                  data-testid="supplier-custom-feature-support-link"
                >
                  or browse the Support Center
                </button>
              </div>
            </div>

            {/* Visual — friendly inbox-style mock so the message lands as
                a real conversation, not a marketing pitch. */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-xl shadow-blue-500/5 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/50">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  </div>
                  <span className="text-[11px] text-gray-400 ml-2 font-medium">Support Center · Feature requests</span>
                </div>
                <div className="p-5 space-y-4">
                  {/* Inbound */}
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">SK</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-gray-400 mb-1">Sarah · Charter operator, Dubai · 2 days ago</div>
                      <div className="bg-gray-50 rounded-xl rounded-tl-sm p-3 text-sm text-gray-700 leading-relaxed">
                        Any chance you could add VAT-compliant invoicing for our UAE clients? We need it on every receipt.
                      </div>
                    </div>
                  </div>
                  {/* Outbound — our reply */}
                  <div className="flex gap-3 flex-row-reverse">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
                      <img src="/logo-icon.png" alt="BookingFlows" className="w-7 h-7 rounded-lg object-cover" />
                    </div>
                    <div className="flex-1 min-w-0 text-right">
                      <div className="text-[11px] text-gray-400 mb-1">BookingFlows · just now</div>
                      <div className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-xl rounded-tr-sm p-3 text-sm leading-relaxed text-left">
                        On it. We'll have a draft for you by Friday — VAT line item on receipts + tenant-level tax config. We'll loop you in on the rollout.
                      </div>
                      <div className="flex items-center gap-1.5 justify-end mt-2 text-[11px] text-emerald-600 font-medium">
                        <Check className="w-3 h-3" strokeWidth={3} />
                        Shipped Friday
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-center text-xs text-gray-400 mt-4">
                Real conversations. Real engineering. Real responsiveness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA — background beams with collision */}
      <BackgroundBeamsWithCollision className="min-h-[34rem] py-28" data-testid="supplier-final-cta">
        <div className="max-w-4xl mx-auto text-center px-4 relative z-20">
          <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-6" dir={isAr ? 'rtl' : undefined}>
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            {isAr ? 'برنامج الحجز الأعلى تقييماً' : 'Top-Rated Booking Software'}
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-[1.05] tracking-tight" dir={isAr ? 'rtl' : undefined}>
            {isAr ? <>سجّل في برنامج الحجوزات<br className="hidden sm:block" /><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500"> — ابدأ مجاناً</span></> : <>Sign up for booking software<br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500"> — free to start</span></>}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-4 max-w-2xl mx-auto leading-relaxed" dir={isAr ? 'rtl' : undefined}>
            {isAr
              ? 'انضم إلى مئات مشغّلي الجولات وشركات الرحلات البحرية والتأجير الذين يستقبلون الحجوزات أونلاين على مدار الساعة.'
              : 'Join hundreds of tour operators, charter companies, and rental businesses taking bookings online 24/7.'}
          </p>
          <p className="text-sm text-blue-600 mb-10" dir={isAr ? 'rtl' : undefined}>
            {isAr ? 'بعد التسجيل، سيتواصل معك فريقنا شخصياً لمساعدتك في الإعداد.' : 'After you sign up, our team will reach out personally to help you get set up.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <NoiseBackground
              containerClassName="w-fit p-[5px] rounded-full"
              gradientColors={["rgb(37, 99, 235)", "rgb(125, 211, 252)", "rgb(29, 78, 216)"]}
            >
              <button
                onClick={() => { trackSignupStarted('supplier_cta_bottom'); navigate('/signup'); }}
                className="inline-flex items-center cursor-pointer rounded-full bg-blue-600 hover:bg-blue-500 px-10 py-4 text-lg font-medium text-white shadow-[0px_2px_0px_0px_rgba(255,255,255,0.22)_inset] transition-[background-color,transform] duration-150 active:scale-[0.98]"
                data-testid="supplier-cta-bottom"
              >
                Sign Up for Booking Software <ArrowRight className="ml-3 w-5 h-5" />
              </button>
            </NoiseBackground>
            <button
              onClick={() => navigate('/demo')}
              className="px-10 py-4 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 text-lg font-medium transition-colors"
              data-testid="supplier-cta-demo"
            >
              {isAr ? 'احجز عرضاً مجانياً' : 'Book a Free Demo'}
            </button>
          </div>
          <p className="text-gray-400 text-sm mt-6">
            {isAr ? 'لا حاجة لبطاقة ائتمان · مجاني للبدء · نقل بيانات مجاني · إعداد خلال 15 دقيقة' : 'No credit card required · Free to start · Free data migration · Setup in 15 minutes'}
          </p>
        </div>
      </BackgroundBeamsWithCollision>

      {/* Footer — full footer mirroring /marketplace for brand congruence.
          Includes social links + customer story link + marketplace cross-link. */}
      <footer className="bg-gray-900 text-gray-400 py-14 pb-[calc(3.5rem+env(safe-area-inset-bottom))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Brand blurb */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/logo-icon.png"
                  alt="BookingFlows"
                  className="w-9 h-9 rounded-lg object-cover"
                />
                <h4 className="text-white font-bold text-lg">BookingFlows</h4>
              </div>
              <p className={`text-sm leading-relaxed ${isAr ? 'text-right' : ''}`} dir={isAr ? 'rtl' : undefined}>
                {isAr
                  ? 'برنامج حجوزات أونلاين للجولات والفعاليات والتجارب وكل نشاط يستقبل الحجوزات.'
                  : 'Online booking software for tours, venues, experiences, and any business that takes reservations.'}
              </p>
              {/* Social row — same set as /marketplace */}
              <div className="mt-5">
                <SocialLinksDark />
              </div>
            </div>

            {/* Product */}
            <div>
              <h5 className="text-white font-semibold mb-4">{isAr ? 'المنتج' : 'Product'}</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/features')} className="hover:text-white transition-colors text-left" data-testid="footer-features-link">All Features</button></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><button onClick={() => navigate('/customers/boatx')} className="hover:text-white transition-colors text-left">BOATX story</button></li>
                <li><button onClick={() => navigate('/customers/banana-boat-rentals')} className="hover:text-white transition-colors text-left">Banana Boat Rentals story</button></li>
                <li><button onClick={() => navigate('/marketplace')} className="hover:text-white transition-colors text-left">Marketplace</button></li>
              </ul>
            </div>

            {/* Software */}
            <div>
              <h5 className="text-white font-semibold mb-4">{isAr ? 'البرنامج' : 'Software'}</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/for-suppliers')} className="hover:text-white transition-colors text-left">Booking Software</button></li>
                <li><button onClick={() => navigate('/for-charters')} className="hover:text-white transition-colors text-left">Charter Booking</button></li>
                <li><button onClick={() => navigate('/for-tours')} className="hover:text-white transition-colors text-left">Tour Booking</button></li>
                <li><button onClick={() => navigate('/for-rentals')} className="hover:text-white transition-colors text-left">Rental Booking</button></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h5 className="text-white font-semibold mb-4">{isAr ? 'الموارد' : 'Resources'}</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/demo')} className="hover:text-white transition-colors text-left" data-testid="footer-demo-link">Book a Demo</button></li>
                <li><button onClick={() => navigate('/referral-program')} className="hover:text-white transition-colors text-left" data-testid="footer-referral-link">Referral Program</button></li>
                <li><button onClick={() => navigate('/onboarding')} className="hover:text-white transition-colors text-left" data-testid="footer-onboarding-link">Onboarding Guide</button></li>
                <li><button onClick={() => navigate('/developers')} className="hover:text-white transition-colors text-left" data-testid="footer-api-docs-link">API Docs</button></li>
                <li><button onClick={() => navigate('/find-booking')} className="hover:text-white transition-colors text-left">Find My Booking</button></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors text-left">Operator Login</button></li>
                <li><button onClick={() => { trackSignupStarted('footer'); navigate('/signup'); }} className="hover:text-white transition-colors text-left">Get Started Free</button></li>
              </ul>
            </div>

            {/* Support / Legal */}
            <div>
              <h5 className="text-white font-semibold mb-4">{isAr ? 'الدعم' : 'Support'}</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/blog')} className="hover:text-white transition-colors text-left" data-testid="footer-blog-link">Blog</button></li>
                <li><button onClick={() => navigate('/support')} className="hover:text-white transition-colors text-left" data-testid="footer-help-center-link">Help Center</button></li>
                <li><a href="/support" className="hover:text-white transition-colors" data-testid="footer-contact-link">Contact Us</a></li>
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors text-left">Terms of Service</button></li>
                <li><button onClick={() => navigate('/delete-account')} data-testid="footer-delete-account-link" className="hover:text-white transition-colors text-left">Delete Account</button></li>
                <li><button onClick={() => navigate('/cookies')} className="hover:text-white transition-colors text-left" data-testid="footer-cookies-link">Cookie Policy</button></li>
                <li><button onClick={() => navigate('/dpa')} className="hover:text-white transition-colors text-left" data-testid="footer-dpa-link">Data Processing (DPA)</button></li>
                <li><button onClick={() => navigate('/acceptable-use')} className="hover:text-white transition-colors text-left" data-testid="footer-aup-link">Acceptable Use</button></li>
                <li><button onClick={() => navigate('/security-policy')} className="hover:text-white transition-colors text-left" data-testid="footer-security-policy-link">Security Policy</button></li>
                <li><button onClick={() => navigate('/vulnerability-disclosure')} className="hover:text-white transition-colors text-left" data-testid="footer-vdp-link">Vulnerability Disclosure</button></li>
                <li><button onClick={() => navigate('/dmca')} className="hover:text-white transition-colors text-left" data-testid="footer-dmca-link">DMCA</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <p>&copy; {new Date().getFullYear()} BookingFlows. All rights reserved.</p>
            <p className="text-gray-500">{isAr ? 'صُنع بواسطة مشغّلين · لأجل المشغّلين' : 'Built by operators · For operators'}</p>
          </div>
          <p className="mt-4 text-xs text-gray-600 text-center sm:text-left">
            Intuit and QuickBooks are registered trademarks of Intuit Inc. Used with permission.
          </p>
        </div>
      </footer>

      {/* Floating "Start free" CTA — appears only after the user scrolls past
          the hero. Bottom-right, mobile-safe (honors iOS bottom inset). Pure
          conversion nudge — no chat, no socials, no bot to monitor. */}
      <div
        className={`fixed right-4 sm:right-6 z-40 transition-all duration-300 ease-out pointer-events-none ${
          showFloatingCta ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button
          onClick={() => { trackSignupStarted('floating_cta'); navigate('/signup'); }}
          className="pointer-events-auto group inline-flex items-center gap-2 pl-5 pr-4 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-xl shadow-blue-500/30 transition-all hover:-translate-y-0.5"
          data-testid="supplier-floating-cta"
          aria-label="Start free"
        >
          <Sparkles className="w-4 h-4" />
          <span>{isAr ? 'ابدأ مجاناً' : 'Start free'}</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
};

export default SupplierFeaturesPage;
